/**
 * CatalogadorPanel - tab "Catalogador IA" dentro de ClientAdminPage.
 *
 * Sub-tabs internos (state-based, NO router):
 *   - config         : prompt versionado + provider/modelo + parámetros + variables
 *   - test           : banco de pruebas (sin persistir)
 *   - history        : histórico de runs + marcas sugeridas
 *   - batch          : ejecutar batch sobre la DB
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Brain, Save, CheckCircle2, FlaskConical, History as HistoryIcon, Play, AlertTriangle,
  Loader2, RefreshCw, Image as ImageIcon, Check, X, ChevronRight, ChevronDown, ExternalLink, Pencil, Download,
  Plus, Trash2, RotateCw, Eye, Copy, GitBranch, FileText, Archive, Star, Send, ArchiveRestore,
  Search, Maximize2, Ban,
} from 'lucide-react';
import api from '../../utils/api';
import ImageModal from '../ImageModal';
import { useColumnDisplayNames } from '../../hooks/useColumnDisplayNames';
import {
  listPrompts, createPrompt, activatePrompt, patchActivePrompt,
  archivePrompt, deletePrompt, listPromptTemplates,
  streamTestBatch, runBatch, listRuns, getRun, getRunItems, cancelRun, markRunCancelled, getRunItemEnrichment,
  fetchProviderModels,
  syncPromptVariable, patchPromptVariable,
  listBrandSuggestions, patchBrandSuggestion,
  listSubcategorySuggestions, patchSubcategorySuggestion,
  listCurationReports, getCurationReport, patchCurationReport,
  formatSemverVersion, compareSemverDesc, nextMinor, nextMajor,
  type CatalogPrompt, type Provider, type RunDoc, type TestItemResult, type TestStreamEvent,
  type ProviderModel, type SyncSource, type VariableSyncDiff, type BrandSuggestion,
  type VersionBump, type CurationReport, type SubcategorySuggestion, type PromptTemplate,
  PROVIDER_MODELS, PROVIDER_LABELS, ACTIVE_PROVIDERS, LEGACY_PROVIDERS,
} from '../../services/catalogAdmin';

// Cache compartido entre instancias del componente. Solo provider-models;
// no info sensible. ttl in-memory hasta refresh manual o reload de página.
const _modelsCache: Partial<Record<Provider, ProviderModel[]>> = {};

/**
 * Hook que devuelve la lista de modelos disponibles para el provider dado.
 * - Si el provider es 'openrouter' o 'cerebras', fetcha del backend.
 * - Si el provider es legacy ('openai'/'gemini'), usa la lista hardcoded.
 * - Si el fetch falla, fallback a PROVIDER_MODELS hardcoded.
 */
function useProviderModels(provider: Provider): { models: string[]; loading: boolean; error: string | null } {
  const [state, setState] = useState<{ models: string[]; loading: boolean; error: string | null }>({
    models: _modelsCache[provider]?.map(m => m.id) || PROVIDER_MODELS[provider] || [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (provider !== 'openrouter' && provider !== 'cerebras') {
      // Legacy: solo lista hardcoded
      setState({ models: PROVIDER_MODELS[provider] || [], loading: false, error: null });
      return;
    }

    if (_modelsCache[provider]) {
      setState({ models: _modelsCache[provider]!.map(m => m.id), loading: false, error: null });
      return;
    }

    setState(s => ({ ...s, loading: true }));
    fetchProviderModels(provider)
      .then(r => {
        if (cancelled) return;
        const models = (r.models || []).filter(m => m.recommended_for_catalog || provider === 'cerebras');
        const ids = models.map(m => m.id);
        _modelsCache[provider] = models;
        setState({ models: ids.length ? ids : PROVIDER_MODELS[provider] || [], loading: false, error: null });
      })
      .catch(e => {
        if (cancelled) return;
        const fallback = PROVIDER_MODELS[provider] || [];
        setState({ models: fallback, loading: false, error: e?.message || 'fetch error' });
      });

    return () => { cancelled = true; };
  }, [provider]);

  return state;
}

interface Props {
  clientId: string;
  clientName: string;
}

type SubTab = 'config' | 'test' | 'history' | 'reports' | 'batch';

const SUBTABS: { id: SubTab; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  {
    id: 'config',
    label: 'Prompts',
    icon: Brain,
    description: 'Editor del prompt y parámetros del cataloguer IA. "Guardar" mutates la versión activa. Para preservar la actual y crear una nueva, usá "Guardar como nueva versión".',
  },
  {
    id: 'test',
    label: 'Banco de pruebas',
    icon: FlaskConical,
    description: 'Probá un prompt sobre productos reales sin guardar a la base. Sirve para validar antes de activar la versión.',
  },
  {
    id: 'history',
    label: 'Histórico',
    icon: HistoryIcon,
    description: 'Log de todos los runs (test, batch, re-catalogado) con costos, errores, y detalle por producto.',
  },
  {
    id: 'reports',
    label: 'Reportes',
    icon: FileText,
    description: 'Reportes automáticos de curación post-batch: marcas nuevas, discrepancias, subcats sugeridas, marcas no whitelist.',
  },
  {
    id: 'batch',
    label: 'Ejecutar batch',
    icon: Play,
    description: 'Procesa todos los productos pendientes (sin entry en `catalog`) o re-cataloga todo. Costo estimado antes de empezar.',
  },
];

const CatalogadorPanel: React.FC<Props> = ({ clientId, clientName }) => {
  const [subtab, setSubtab] = useState<SubTab>('config');
  const activeMeta = SUBTABS.find(t => t.id === subtab)!;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-purple-600" />
          <h3 className="text-base font-semibold text-gray-900">Catalogador IA</h3>
          <span className="text-xs text-gray-400">— {clientName}</span>
        </div>
      </div>

      {/* Subtabs */}
      <div className="flex border-b border-gray-200 px-2">
        {SUBTABS.map(t => {
          const Icon = t.icon;
          const active = subtab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSubtab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${
                active
                  ? 'border-purple-600 text-purple-700 font-medium'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panel descriptivo de la tab activa */}
      <div className="px-4 pt-3">
        <div className="bg-indigo-50 border-l-2 border-indigo-300 rounded px-3 py-2 text-xs text-gray-600">
          {activeMeta.description}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {subtab === 'config'  && <ConfigSubtab clientId={clientId} />}
        {subtab === 'test'    && <TestBenchSubtab clientId={clientId} />}
        {subtab === 'history' && <HistorySubtab clientId={clientId} />}
        {subtab === 'reports' && <ReportsSubtab clientId={clientId} />}
        {subtab === 'batch'   && <BatchSubtab clientId={clientId} />}
      </div>
    </div>
  );
};

export default CatalogadorPanel;

// =============================================================================
// CONFIG (prompts versionados)
// =============================================================================

const ConfigSubtab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [prompts, setPrompts] = useState<CatalogPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // editor state
  const [draftPrompt, setDraftPrompt] = useState('');
  const [draftName, setDraftName] = useState('');
  const [provider, setProvider] = useState<Provider>('cerebras');
  const [model, setModel] = useState('gpt-oss-120b');
  const [temperature, setTemperature] = useState(0.3);
  const [maxTokens, setMaxTokens] = useState(2000);
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
  // Concurrencia: cantidad de llamados al LLM en paralelo durante batch.
  const [concurrency, setConcurrency] = useState(5);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  // Modal "Guardar como nueva versión"
  const [saveAsNewModal, setSaveAsNewModal] = useState(false);
  const [versionBump, setVersionBump] = useState<VersionBump>('minor');
  const [versionExplicit, setVersionExplicit] = useState<string>('');
  const [versionExplicitErr, setVersionExplicitErr] = useState<string | null>(null);
  const [newVersionName, setNewVersionName] = useState('');

  // Empty state + templates
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('empty');

  // Sección archivadas (collapsable)
  const [showArchived, setShowArchived] = useState(false);

  // Modal eliminar permanente
  const [deleteConfirm, setDeleteConfirm] = useState<CatalogPrompt | null>(null);

  // Toast simple para feedback
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 1500);
    return () => clearTimeout(id);
  }, [toast]);

  const isLegacyProvider = LEGACY_PROVIDERS.includes(provider);

  // Versión activa actual (para vista solo-lectura y cálculo de bumps).
  const activePrompt = useMemo(() => prompts.find(p => p.isActive) || null, [prompts]);
  const selectedPrompt = useMemo(
    () => (selectedId ? prompts.find(p => p.id === selectedId) : null) || null,
    [prompts, selectedId],
  );

  // Modo solo-lectura cuando la versión seleccionada NO es la activa.
  const isReadOnly = !!selectedPrompt && !selectedPrompt.isActive;

  // CAMBIO #9: detectar cambios sin guardar (draft != activa).
  const hasUnsavedChanges = useMemo(() => {
    if (!selectedPrompt || !selectedPrompt.isActive) return false;
    return (
      draftPrompt !== (selectedPrompt.prompt || '') ||
      draftName !== (selectedPrompt.name || '') ||
      provider !== selectedPrompt.provider ||
      model !== selectedPrompt.model ||
      Math.abs(temperature - selectedPrompt.temperature) > 1e-9 ||
      maxTokens !== selectedPrompt.maxTokens ||
      Math.abs(confidenceThreshold - selectedPrompt.confidenceThreshold) > 1e-9 ||
      concurrency !== (selectedPrompt.concurrency ?? 5)
    );
  }, [selectedPrompt, draftPrompt, draftName, provider, model, temperature, maxTokens, confidenceThreshold, concurrency]);

  // CAMBIO #9: warn antes de cerrar/recargar con cambios sin guardar.
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const reload = async () => {
    setLoading(true);
    try {
      const { prompts } = await listPrompts(clientId);
      setPrompts(prompts);
      const active = prompts.find(p => p.isActive) || prompts[0];
      if (active) {
        setSelectedId(active.id);
        loadIntoEditor(active);
      } else {
        setSelectedId(null);
        setDraftPrompt('');
        setDraftName('');
      }
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // CAMBIO #6: cargar templates si no hay prompts.
  useEffect(() => {
    if (prompts.length === 0 && !loading) {
      listPromptTemplates(clientId)
        .then(r => setTemplates(r.templates || []))
        .catch(() => setTemplates([]));
    }
  }, [prompts.length, loading, clientId]);

  const loadIntoEditor = (p: CatalogPrompt) => {
    setDraftPrompt(p.prompt);
    setDraftName(p.name || '');
    setProvider(p.provider);
    setModel(p.model);
    setTemperature(p.temperature ?? 0.3);
    setMaxTokens(p.maxTokens ?? 2000);
    setConfidenceThreshold(p.confidenceThreshold ?? 0.7);
    setConcurrency(typeof p.concurrency === 'number' && p.concurrency > 0 ? p.concurrency : 5);
  };

  const trySwitchPrompt = (p: CatalogPrompt) => {
    if (hasUnsavedChanges) {
      const ok = window.confirm('Cambios sin guardar. ¿Descartar?');
      if (!ok) return;
    }
    setSelectedId(p.id);
    loadIntoEditor(p);
  };

  // Etiquetas dinámicas para el selector de versión nueva: "v5.2", "v6.0".
  const previewMinor = activePrompt ? nextMinor(activePrompt.version) : '1.0';
  const previewMajor = activePrompt ? nextMajor(activePrompt.version) : '1.0';

  const handleCopyPrompt = async (p: CatalogPrompt) => {
    try {
      await navigator.clipboard.writeText(p.prompt || '');
      setToast('Copiado');
    } catch {
      setToast('No se pudo copiar');
    }
  };

  // "Activar esta versión" desde el banner read-only (no toca contenido).
  const handleActivateSelected = async () => {
    if (!selectedPrompt) return;
    try {
      await activatePrompt(clientId, selectedPrompt.version);
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    }
  };

  // "Clonar para editar" desde el banner read-only: crea minor con este
  // contenido como base y la marca como activa, así el editor queda editable.
  const handleCloneSelected = async () => {
    if (!selectedPrompt) return;
    setSaving(true);
    setErrMsg(null);
    try {
      await createPrompt(clientId, {
        prompt: selectedPrompt.prompt,
        provider: selectedPrompt.provider,
        model: selectedPrompt.model,
        temperature: selectedPrompt.temperature,
        maxTokens: selectedPrompt.maxTokens,
        confidenceThreshold: selectedPrompt.confidenceThreshold,
        concurrency: selectedPrompt.concurrency,
        activate: true,
        versionBump: 'minor',
      });
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [clientId]);

  // Modelos disponibles del provider (cargados del backend con fallback)
  const { models: availableModels, loading: modelsLoading, error: modelsError } = useProviderModels(provider);

  // Si cambia provider/lista, ajustar model si quedó fuera
  useEffect(() => {
    if (availableModels.length > 0 && !availableModels.includes(model)) {
      setModel(availableModels[0]);
    }
    // eslint-disable-next-line
  }, [provider, availableModels.length]);

  // CAMBIO #9: PATCH active — mutates active version, NO crea nueva.
  const handleSaveActive = async () => {
    if (!selectedPrompt || !selectedPrompt.isActive) {
      setErrMsg('Solo se puede mutar la versión activa.');
      return;
    }
    setSaving(true);
    setErrMsg(null);
    try {
      await patchActivePrompt(clientId, selectedPrompt.version, {
        prompt: draftPrompt,
        name: draftName.trim() || undefined,
        provider, model, temperature, maxTokens, confidenceThreshold, concurrency,
      });
      setToast('Versión activa actualizada');
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // CAMBIO #9: POST new version — Save As New.
  const handleSaveAsNew = async () => {
    if (versionBump === 'explicit') {
      if (!/^\d+\.\d+$/.test(versionExplicit.trim())) {
        setVersionExplicitErr('Formato inválido. Usá X.Y (ej: 5.2)');
        return;
      }
      setVersionExplicitErr(null);
    }

    setSaving(true);
    setErrMsg(null);
    try {
      const trimmedName = newVersionName.trim() || draftName.trim();
      await createPrompt(clientId, {
        prompt: draftPrompt,
        name: trimmedName ? trimmedName : undefined,
        provider, model, temperature, maxTokens, confidenceThreshold, concurrency,
        activate: true,
        versionBump,
        versionExplicit: versionBump === 'explicit' ? versionExplicit.trim() : undefined,
        // BUG-FIX 2026-05-13: pasar la versión seleccionada como base de herencia.
        // Antes el backend caía al fallback `where(isActive,==,true)` que falla cuando
        // el flag está inconsistente (algunos docs tienen `active` en lugar de `isActive`).
        // Resultado: la nueva versión perdía las `variables` (BRANDS custom, etc.).
        basedOnVersion: selectedPrompt?.version ?? selectedId ?? undefined,
      });
      setSaveAsNewModal(false);
      setVersionExplicit('');
      setNewVersionName('');
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // CAMBIO #6: Crear primer prompt desde template.
  const handleCreateFromTemplate = async () => {
    const tpl = templates.find(t => t.id === selectedTemplateId) || templates[0];
    if (!tpl) return;
    setSaving(true);
    setErrMsg(null);
    try {
      await createPrompt(clientId, {
        prompt: tpl.prompt || '',
        name: tpl.label,
        provider: 'cerebras',
        model: 'gpt-oss-120b',
        temperature: 0.3,
        maxTokens: 2000,
        confidenceThreshold: 0.7,
        activate: true,
        versionBump: 'minor',
      });
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // CAMBIO #5: Archivar / restaurar / eliminar.
  const handleArchive = async (p: CatalogPrompt, archived: boolean) => {
    try {
      await archivePrompt(clientId, p.version, archived);
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    }
  };

  const handleDeletePermanent = async () => {
    if (!deleteConfirm) return;
    try {
      await deletePrompt(clientId, deleteConfirm.version);
      setDeleteConfirm(null);
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    }
  };

  const handleActivate = async (version: string | number) => {
    try {
      await activatePrompt(clientId, version);
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    }
  };

  if (loading) return <div className="text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" />Cargando...</div>;

  // CAMBIO #6: Empty state cuando no hay versiones
  if (prompts.length === 0) {
    return (
      <div className="border border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
        <div className="flex items-center gap-2 mb-2">
          <Brain className="w-5 h-5 text-purple-600" />
          <h4 className="text-sm font-semibold text-gray-800">Sin prompt configurado</h4>
        </div>
        <div className="text-xs text-gray-600 mb-4">
          Este cliente todavía no tiene una versión del prompt. Elegí una plantilla para empezar.
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-600 mb-1">Plantilla</label>
            <select
              value={selectedTemplateId}
              onChange={e => setSelectedTemplateId(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
            >
              {templates.length === 0 && <option value="empty">Vacío</option>}
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleCreateFromTemplate}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Crear desde plantilla
          </button>
        </div>
        {errMsg && <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{errMsg}</div>}
      </div>
    );
  }

  // CAMBIO #5: agrupar versiones en 3 secciones
  const sortedPrompts = prompts.slice().sort((a, b) => compareSemverDesc(a.version, b.version));
  const activeList = sortedPrompts.filter(p => p.isActive);
  const archivedList = sortedPrompts.filter(p => !p.isActive && p.archived === true);
  const presetsList = sortedPrompts.filter(p => !p.isActive && p.archived !== true);

  const renderVersionRow = (p: CatalogPrompt, kind: 'active' | 'preset' | 'archived') => {
    const isSel = selectedId === p.id;
    const semver = formatSemverVersion(p.version);
    const baseName = p.name && p.name.trim() ? p.name : '';
    const title = baseName ? `V${semver} — ${baseName}` : `V${semver}`;
    return (
      <div
        key={p.id}
        className={`relative border-b border-gray-200 last:border-b-0 ${
          isSel ? 'bg-white border-l-2 border-l-purple-600' : 'hover:bg-white'
        }`}
      >
        <button
          onClick={() => trySwitchPrompt(p)}
          className="w-full text-left px-3 py-2 pr-9 text-xs"
        >
          <div className="flex items-center justify-between gap-1">
            <span className="font-semibold truncate" title={title}>{title}</span>
            {kind === 'active' && (
              <span className="shrink-0 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[10px] font-medium">ACTIVA</span>
            )}
            {kind === 'archived' && (
              <span className="shrink-0 px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-[10px] font-medium">ARCH</span>
            )}
          </div>
          <div className="text-[10px] text-gray-400 tabular-nums mt-0.5">{new Date(p.createdAt).toLocaleString('es-AR', { hour12: false })}</div>
          <div className="text-[10px] text-gray-500">v{semver} · {p.provider} · {p.model}</div>
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleCopyPrompt(p); }}
          title="Copiar prompt al clipboard"
          className="absolute top-1.5 right-1.5 p-1 rounded text-gray-400 hover:text-purple-700 hover:bg-purple-50"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        {/* Acciones por kind */}
        <div className="px-3 pb-2 flex flex-wrap gap-1">
          {kind === 'preset' && (
            <>
              <button onClick={() => handleActivate(p.version)}
                      className="text-[10px] px-1.5 py-0.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded">
                Activar
              </button>
              <button onClick={() => handleArchive(p, true)}
                      className="text-[10px] px-1.5 py-0.5 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded inline-flex items-center gap-1">
                <Archive className="w-3 h-3" /> Archivar
              </button>
            </>
          )}
          {kind === 'archived' && (
            <>
              <button onClick={() => handleArchive(p, false)}
                      className="text-[10px] px-1.5 py-0.5 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded inline-flex items-center gap-1">
                <ArchiveRestore className="w-3 h-3" /> Restaurar
              </button>
              <button onClick={() => setDeleteConfirm(p)}
                      className="text-[10px] px-1.5 py-0.5 border border-red-300 text-red-700 hover:bg-red-50 rounded inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Eliminar
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Versiones — 3 secciones: Activa / Presets / Archivadas */}
      <div className="col-span-3">
        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Versiones</div>

        {/* Activa */}
        <div className="mb-3">
          <div className="flex items-center gap-1 text-[10px] font-semibold text-emerald-700 uppercase mb-1">
            <Star className="w-3 h-3" /> Activa ({activeList.length})
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
            {activeList.length === 0 ? (
              <div className="p-2 text-[11px] text-gray-500 italic">Sin versión activa.</div>
            ) : (
              activeList.map(p => renderVersionRow(p, 'active'))
            )}
          </div>
        </div>

        {/* Presets */}
        <div className="mb-3">
          <div className="text-[10px] font-semibold text-gray-700 uppercase mb-1">
            📑 Presets ({presetsList.length})
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 max-h-[200px] overflow-y-auto">
            {presetsList.length === 0 ? (
              <div className="p-2 text-[11px] text-gray-500 italic">Sin presets disponibles.</div>
            ) : (
              presetsList.map(p => renderVersionRow(p, 'preset'))
            )}
          </div>
        </div>

        {/* Archivadas (collapsable) */}
        <div>
          <button
            onClick={() => setShowArchived(v => !v)}
            className="w-full flex items-center justify-between text-[10px] font-semibold text-gray-500 uppercase mb-1 hover:text-gray-700"
          >
            <span className="inline-flex items-center gap-1">
              <Archive className="w-3 h-3" /> Archivadas ({archivedList.length})
            </span>
            {showArchived ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
          {showArchived && (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50 max-h-[200px] overflow-y-auto">
              {archivedList.length === 0 ? (
                <div className="p-2 text-[11px] text-gray-500 italic">Sin versiones archivadas.</div>
              ) : (
                archivedList.map(p => renderVersionRow(p, 'archived'))
              )}
            </div>
          )}
        </div>

        {toast && (
          <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 inline-flex items-center gap-1">
            <Check className="w-3 h-3" />{toast}
          </div>
        )}
      </div>

      {/* Editor */}
      <div className="col-span-9 space-y-3">
        {/* Banner solo-lectura (versión NO-activa) — fondo amber + acciones */}
        {isReadOnly && selectedPrompt && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3">
            <Eye className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 text-xs text-amber-900">
              <div className="font-semibold">
                Viendo V{formatSemverVersion(selectedPrompt.version)} — solo lectura.
                {activePrompt && <> La activa es V{formatSemverVersion(activePrompt.version)}.</>}
              </div>
              <div className="text-amber-800/80 mt-0.5">
                Para modificar, activá esta versión o cloná su contenido en una nueva minor.
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={handleActivateSelected}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Activar esta versión
              </button>
              <button
                onClick={handleCloneSelected}
                disabled={saving}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-amber-300 hover:bg-amber-100 text-amber-900 rounded disabled:opacity-50"
              >
                <GitBranch className="w-3.5 h-3.5" />
                Clonar para editar
              </button>
            </div>
          </div>
        )}

        <Field label="Nombre de la versión">
          <input
            type="text"
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            placeholder="Ej: prompt con whitelist refinada"
            disabled={isReadOnly}
            className={`w-full text-sm border border-gray-300 rounded px-2 py-1.5 ${isReadOnly ? 'opacity-70 cursor-not-allowed bg-gray-50' : ''}`}
          />
          <div className="text-[10px] text-gray-400 mt-0.5">
            Opcional. Si lo dejás vacío, el backend asigna <code>V&lt;X.Y&gt; · &lt;timestamp&gt;</code>.
          </div>
        </Field>

        <div className="grid grid-cols-3 gap-2">
          <Field label="Provider">
            <select value={provider} onChange={e => setProvider(e.target.value as Provider)}
                    disabled={isReadOnly}
                    className={`w-full text-sm border border-gray-300 rounded px-2 py-1.5 ${isReadOnly ? 'opacity-70 cursor-not-allowed bg-gray-50' : ''}`}>
              {ACTIVE_PROVIDERS.map(p => (
                <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
              ))}
              {/* Si la versión cargada tiene un provider legacy (openai/gemini),
                  mostramos la opción para no perder el valor, pero indicamos
                  que debe migrarse a Cerebras u OpenRouter al guardar. */}
              {isLegacyProvider && (
                <option value={provider}>{PROVIDER_LABELS[provider]}</option>
              )}
            </select>
            {isLegacyProvider && (
              <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-1">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                <span>Provider legacy — cambia a Cerebras u OpenRouter al guardar.</span>
              </div>
            )}
          </Field>
          <Field label="Modelo">
            <select value={model} onChange={e => setModel(e.target.value)}
                    disabled={modelsLoading || isReadOnly}
                    className={`w-full text-sm border border-gray-300 rounded px-2 py-1.5 ${isReadOnly ? 'opacity-70 cursor-not-allowed bg-gray-50' : ''}`}>
              {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {modelsLoading && (
              <div className="mt-1 text-[10px] text-gray-400 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />Cargando modelos...
              </div>
            )}
            {modelsError && (
              <div className="mt-1 text-[10px] text-amber-700">
                No pude cargar lista live, usando fallback.
              </div>
            )}
          </Field>
          <Field label={`Confidence threshold (${confidenceThreshold.toFixed(2)})`}>
            <input type="range" min={0} max={1} step={0.05} value={confidenceThreshold}
                   onChange={e => setConfidenceThreshold(parseFloat(e.target.value))}
                   disabled={isReadOnly}
                   className={`w-full ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`} />
          </Field>
          <Field label={`Temperature (${temperature.toFixed(2)})`}>
            <input type="range" min={0} max={1} step={0.05} value={temperature}
                   onChange={e => setTemperature(parseFloat(e.target.value))}
                   disabled={isReadOnly}
                   className={`w-full ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`} />
          </Field>
          <Field label="Max tokens">
            <input type="number" value={maxTokens} onChange={e => setMaxTokens(parseInt(e.target.value || '0'))}
                   disabled={isReadOnly}
                   className={`w-full text-sm border border-gray-300 rounded px-2 py-1.5 tabular-nums ${isReadOnly ? 'opacity-70 cursor-not-allowed bg-gray-50' : ''}`} />
          </Field>
          <Field label="Concurrencia (llamadas LLM en paralelo)">
            <input
              type="number"
              min={1}
              max={32}
              step={1}
              value={concurrency}
              onChange={e => setConcurrency(Math.max(1, Math.min(32, parseInt(e.target.value || '1') || 1)))}
              disabled={isReadOnly}
              title="Cantidad de calls al LLM en paralelo durante batch. Cerebras: 8 seguro. Gemini paid: 10-15. Default 5."
              className={`w-full text-sm border border-gray-300 rounded px-2 py-1.5 tabular-nums ${isReadOnly ? 'opacity-70 cursor-not-allowed bg-gray-50' : ''}`}
            />
          </Field>
        </div>

        <Field label="Prompt">
          <textarea value={draftPrompt} onChange={e => setDraftPrompt(e.target.value)}
                    readOnly={isReadOnly}
                    className={`w-full text-xs font-mono border border-gray-300 rounded p-2 h-[280px] ${isReadOnly ? 'opacity-70 cursor-not-allowed bg-gray-50' : ''}`}
                    placeholder="Contexto del cliente, reglas de marcas/categorías, instrucciones específicas..." />
        </Field>

        {errMsg && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{errMsg}</div>
        )}

        {/* CAMBIO #9: Banner unsaved changes (solo cuando estás editando la activa) */}
        {!isReadOnly && hasUnsavedChanges && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-amber-900">
              Tenés cambios sin guardar.
            </div>
          </div>
        )}

        {/* CAMBIO #9: Botones — "Guardar" (mutate active) + "Guardar como nueva" */}
        {!isReadOnly && (
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setSaveAsNewModal(true)}
                    disabled={saving || !draftPrompt.trim()}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 rounded">
              <GitBranch className="w-4 h-4" />
              Guardar como nueva versión
            </button>
            <button onClick={handleSaveActive}
                    disabled={saving || !draftPrompt.trim() || !hasUnsavedChanges}
                    title="Mutates the active version. Does NOT create a new version."
                    className="flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar
            </button>
          </div>
        )}

        {/* Variables del prompt — actúa sobre la versión seleccionada.
            Se muestra incluso en read-only (la edición de variables sí va al
            doc seleccionado; podríamos bloquearla también, pero por ahora
            mantenemos el flujo existente para no cambiar el alcance). */}
        {selectedId && (() => {
          const sel = prompts.find(p => p.id === selectedId);
          if (!sel) return null;
          return (
            <PromptVariablesEditor
              clientId={clientId}
              prompt={sel}
              promptText={draftPrompt}
              onChanged={reload}
            />
          );
        })()}
      </div>

      {/* CAMBIO #9: Modal "Guardar como nueva versión" */}
      {saveAsNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-md w-full mx-4 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-base font-semibold text-gray-900">Guardar como nueva versión</h4>
              <button onClick={() => setSaveAsNewModal(false)} className="text-gray-500 hover:text-gray-900">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo de bump</label>
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-2 text-xs">
                    <input type="radio" checked={versionBump === 'minor'} onChange={() => setVersionBump('minor')} />
                    Minor <span className="text-gray-500 tabular-nums">(v{previewMinor})</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="radio" checked={versionBump === 'major'} onChange={() => setVersionBump('major')} />
                    Major <span className="text-gray-500 tabular-nums">(v{previewMajor})</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input type="radio" checked={versionBump === 'explicit'} onChange={() => setVersionBump('explicit')} />
                    Personalizada
                  </label>
                  {versionBump === 'explicit' && (
                    <input type="text" value={versionExplicit}
                           onChange={e => { setVersionExplicit(e.target.value); setVersionExplicitErr(null); }}
                           placeholder="X.Y"
                           className="w-32 text-xs tabular-nums border border-gray-300 rounded px-2 py-1 ml-5" />
                  )}
                  {versionExplicitErr && <span className="text-[10px] text-red-700">{versionExplicitErr}</span>}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre (opcional)</label>
                <input type="text" value={newVersionName} onChange={e => setNewVersionName(e.target.value)}
                       placeholder="Ej: prompt con whitelist refinada"
                       className="w-full text-sm border border-gray-300 rounded px-2 py-1.5" />
              </div>
              <div className="text-[11px] text-gray-500">
                La nueva versión se creará y se marcará como activa. La versión actual pasará a Presets.
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setSaveAsNewModal(false)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleSaveAsNew} disabled={saving}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Crear versión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CAMBIO #5: Modal eliminar permanente (con warning) */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-red-200 max-w-md w-full mx-4 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h4 className="text-base font-semibold text-red-700">Eliminar permanentemente</h4>
            </div>
            <div className="text-sm text-gray-700 mb-3">
              Esta acción no se puede deshacer. Los runs históricos van a conservar el label{' '}
              <code className="bg-gray-100 px-1 rounded">V{formatSemverVersion(deleteConfirm.version)}</code>{' '}
              como referencia, pero el contenido del prompt y de las variables se pierde para siempre. ¿Confirmás?
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleDeletePermanent}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded">
                <Trash2 className="w-4 h-4" /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// PromptVariablesEditor — edición curada de variables {{NAME}} del prompt
// =============================================================================

const VAR_SOURCE_LABELS: Record<SyncSource, string> = {
  distinct_marca: 'Marca distinct (última toma)',
  distinct_cat: 'Cat distinct (última toma)',
  distinct_tag_value: 'Tag value distinct',
  firestore_brandHierarchy: 'Firestore brandHierarchy',
  static: 'Estático (sin sync)',
};

// Tokens "del producto" — no son variables editables (los reemplaza el service por-row).
const PRODUCT_TOKENS = new Set(['TITLE', 'MARCA_ORIGEN', 'CAT_ORIGEN', 'SITIO']);

interface PromptVariablesEditorProps {
  clientId: string;
  prompt: CatalogPrompt;
  promptText: string; // texto actual en el editor (puede diferir del guardado)
  onChanged: () => void | Promise<void>;
}

const PromptVariablesEditor: React.FC<PromptVariablesEditorProps> = ({
  clientId, prompt, promptText, onChanged,
}) => {
  // Detectar tokens en el prompt (excluyendo product tokens).
  const detectedTokens = useMemo(() => {
    const re = /\{\{(\w+)\}\}/g;
    const found = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = re.exec(promptText)) !== null) {
      const name = m[1];
      if (!PRODUCT_TOKENS.has(name)) found.add(name);
    }
    return Array.from(found);
  }, [promptText]);

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 mt-2">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Variables del prompt
        </div>
        <div className="text-[10px] text-gray-500">
          Tokens detectados: {detectedTokens.length === 0 ? 'ninguno' : detectedTokens.map(t => `{{${t}}}`).join(', ')}
        </div>
      </div>

      {detectedTokens.length === 0 && (
        <div className="text-xs text-gray-500 italic">
          El prompt no usa variables tipo <code>{'{{NAME}}'}</code> (excluyendo TITLE/MARCA_ORIGEN/CAT_ORIGEN/SITIO).
        </div>
      )}

      <div className="space-y-2">
        {detectedTokens.map(name => {
          const conf = prompt.variables?.[name];
          const undefined_ = !conf;
          return (
            <VariableCard
              key={name}
              clientId={clientId}
              promptVersion={prompt.version}
              varName={name}
              variable={conf}
              undefined_={undefined_}
              onChanged={onChanged}
            />
          );
        })}
      </div>
    </div>
  );
};

const VariableCard: React.FC<{
  clientId: string;
  promptVersion: string | number;
  varName: string;
  variable?: CatalogPrompt['variables'] extends infer V ? V extends Record<string, infer T> ? T : never : never;
  undefined_: boolean;
  onChanged: () => void | Promise<void>;
}> = ({ clientId, promptVersion, varName, variable, undefined_, onChanged }) => {
  const [values, setValues] = useState<string[]>(variable?.values || []);
  const [syncSource, setSyncSource] = useState<SyncSource>(variable?.syncSource || 'static');
  const [syncParam, setSyncParam] = useState<string>(variable?.syncParam || '');
  const [newVal, setNewVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [diff, setDiff] = useState<VariableSyncDiff | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setValues(variable?.values || []);
    setSyncSource(variable?.syncSource || 'static');
    setSyncParam(variable?.syncParam || '');
  }, [variable]);

  const addValue = (v: string) => {
    const t = v.trim();
    if (!t) return;
    if (values.some(x => x.toLowerCase() === t.toLowerCase())) return;
    setValues([...values, t]);
  };
  const removeValue = (v: string) => setValues(values.filter(x => x !== v));

  const persistValues = async (next: string[], source?: SyncSource, param?: string) => {
    setSaving(true);
    setErr(null);
    try {
      await patchPromptVariable(clientId, promptVersion, varName, {
        values: next,
        syncSource: source ?? syncSource,
        syncParam: param ?? (syncParam || null),
      });
      await onChanged();
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAndPersist = async () => {
    const t = newVal.trim();
    if (!t) return;
    if (values.some(x => x.toLowerCase() === t.toLowerCase())) {
      setNewVal('');
      return;
    }
    const next = [...values, t];
    setValues(next);
    setNewVal('');
    await persistValues(next);
  };

  const handleRemoveAndPersist = async (v: string) => {
    const next = values.filter(x => x !== v);
    setValues(next);
    await persistValues(next);
  };

  const handleChangeSource = (src: SyncSource) => {
    // Cambio local. La persistencia ocurre cuando el usuario hace una acción
    // explícita (agregar/quitar valor, aceptar diff de sincronización). Evita
    // re-fetch del panel padre que pisa cambios sin guardar en otros campos.
    setSyncSource(src);
  };

  const handleSync = async () => {
    setSyncing(true);
    setErr(null);
    setDiff(null);
    try {
      const r = await syncPromptVariable(clientId, promptVersion, varName, {
        syncSource,
        syncParam: syncSource === 'distinct_tag_value' ? syncParam : null,
      });
      setDiff(r);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const acceptAllDiff = async () => {
    if (!diff) return;
    const merged = Array.from(new Set([
      ...values.filter(v => !diff.removed.some(r => r.toLowerCase() === v.toLowerCase())),
      ...diff.added,
    ]));
    setValues(merged);
    await persistValues(merged);
    setDiff(null);
  };

  const lastSyncText = variable?.lastSyncedAt
    ? new Date(variable.lastSyncedAt).toLocaleString('es-AR', { hour12: false })
    : null;

  return (
    <div className="border border-gray-200 rounded bg-white p-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <code className="text-xs font-semibold text-purple-700">{`{{${varName}}}`}</code>
          {undefined_ && (
            <span className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">
              <AlertTriangle className="w-3 h-3 inline mr-0.5" />
              Sin definir
            </span>
          )}
          <span className="text-[10px] text-gray-500 tabular-nums">{values.length} valores</span>
        </div>
        {lastSyncText && (
          <span className="text-[10px] text-gray-400">Última sync: {lastSyncText}</span>
        )}
      </div>

      {/* Chips */}
      <div className="flex flex-wrap gap-1">
        {values.length === 0 && (
          <span className="text-[11px] text-gray-400 italic">Sin valores. Agregá manualmente o sincronizá con datos.</span>
        )}
        {values.map(v => (
          <span key={v} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">
            {v}
            <button onClick={() => handleRemoveAndPersist(v)} disabled={saving}
                    className="text-gray-400 hover:text-red-600 disabled:opacity-50" title="Quitar">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>

      {/* Add input */}
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={newVal}
          onChange={e => setNewVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddAndPersist(); } }}
          placeholder="+ Agregar valor"
          className="flex-1 text-xs border border-gray-300 rounded px-2 py-1"
        />
        <button onClick={handleAddAndPersist} disabled={saving || !newVal.trim()}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-700 hover:bg-gray-800 disabled:bg-gray-300 text-white rounded">
          <Plus className="w-3 h-3" /> Agregar
        </button>
      </div>

      {/* Source + sync */}
      <div className="flex items-end gap-2 pt-1 border-t border-gray-100">
        <div className="flex-1">
          <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Fuente sync</label>
          <select value={syncSource} onChange={e => handleChangeSource(e.target.value as SyncSource)}
                  className="w-full text-xs border border-gray-300 rounded px-2 py-1">
            {(Object.entries(VAR_SOURCE_LABELS) as [SyncSource, string][]).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>
        {syncSource === 'distinct_tag_value' && (
          <div className="flex-1">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-0.5">Tag key</label>
            <input type="text" value={syncParam}
                   onChange={e => setSyncParam(e.target.value)}
                   placeholder="Ej: marca_canonica"
                   className="w-full text-xs border border-gray-300 rounded px-2 py-1" />
          </div>
        )}
        <button onClick={handleSync} disabled={syncing || syncSource === 'static'}
                className="flex items-center gap-1 px-2 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded">
          {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
          Sincronizar
        </button>
      </div>

      {/* Diff modal inline */}
      {diff && (
        <div className="border border-blue-200 bg-blue-50 rounded p-2 text-xs space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-blue-900">Diff propuesto ({diff.source})</span>
            <button onClick={() => setDiff(null)} className="text-gray-500 hover:text-gray-900">
              <X className="w-3 h-3" />
            </button>
          </div>
          {diff.added.length > 0 && (
            <div>
              <span className="text-emerald-700 font-semibold">+{diff.added.length} agregar:</span>{' '}
              <span className="text-gray-700">{diff.added.slice(0, 30).join(', ')}{diff.added.length > 30 ? '...' : ''}</span>
            </div>
          )}
          {diff.removed.length > 0 && (
            <div>
              <span className="text-red-700 font-semibold">-{diff.removed.length} quitar:</span>{' '}
              <span className="text-gray-700">{diff.removed.slice(0, 30).join(', ')}{diff.removed.length > 30 ? '...' : ''}</span>
            </div>
          )}
          {diff.added.length === 0 && diff.removed.length === 0 && (
            <div className="text-gray-600 italic">Sin cambios — los valores actuales coinciden con la fuente.</div>
          )}
          {(diff.added.length > 0 || diff.removed.length > 0) && (
            <div className="flex gap-2 pt-1">
              <button onClick={acceptAllDiff} disabled={saving}
                      className="px-2 py-1 text-[11px] bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded">
                Aceptar todo
              </button>
              <button onClick={() => setDiff(null)}
                      className="px-2 py-1 text-[11px] border border-gray-300 hover:bg-gray-100 rounded">
                Editar manualmente
              </button>
            </div>
          )}
        </div>
      )}

      {err && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-1.5">{err}</div>}
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

// Helpers compartidos para selección de tomas y randomización.
const TOMA_REGEX = /^toma_(\d+)_/;

function getTomaNumber(name: string): number | null {
  const m = name.match(TOMA_REGEX);
  return m ? parseInt(m[1], 10) : null;
}

function filterAndSortTomaTables(tables: string[]): string[] {
  return tables
    .filter(t => TOMA_REGEX.test(t))
    .sort((a, b) => (getTomaNumber(b) ?? 0) - (getTomaNumber(a) ?? 0));
}

function tomaLabel(name: string): string {
  const n = getTomaNumber(name);
  return n != null ? `Toma ${n} — ${name}` : name;
}

// Shuffle Fisher-Yates puro (in-place sobre copia).
function shuffleArray<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Sampleo random ESTRATIFICADO por categoría top (`Cat`).
 * Garantiza un mínimo del `minRatioPerCat` (default 15%) de la muestra desde
 * CADA categoría presente en el pool. El resto se completa al azar entre los
 * productos sobrantes.
 *
 * Edge cases:
 *  - pool ≤ n: shuffle puro, devuelve todo.
 *  - cats * minPerCat > n: capa el mínimo a floor(n / cats) para que entren todas.
 *  - cat con menos productos que la cuota: toma todos los que tiene; el resto
 *    se redistribuye al fill aleatorio.
 */
function stratifiedSampleByCat<T extends { Cat?: string | null }>(
  pool: T[],
  n: number,
  minRatioPerCat = 0.15,
): T[] {
  if (pool.length <= n) return shuffleArray(pool);

  const byCat = new Map<string, T[]>();
  for (const p of pool) {
    const key = (p.Cat || '').toString().trim() || '__sin_cat__';
    if (!byCat.has(key)) byCat.set(key, []);
    byCat.get(key)!.push(p);
  }

  const cats = Array.from(byCat.keys());
  const maxFeasible = cats.length > 0 ? Math.floor(n / cats.length) : n;
  const minPerCat = Math.max(1, Math.min(Math.ceil(minRatioPerCat * n), maxFeasible));

  const picked: T[] = [];
  const remaining: T[] = [];
  const buckets: T[][] = Array.from(byCat.values());
  for (const arr of buckets) {
    const shuffled: T[] = shuffleArray(arr);
    const quota = Math.min(minPerCat, shuffled.length);
    picked.push(...shuffled.slice(0, quota));
    remaining.push(...shuffled.slice(quota));
  }

  const slots = n - picked.length;
  if (slots > 0) {
    picked.push(...shuffleArray(remaining).slice(0, slots));
  }

  return shuffleArray(picked);
}

// =============================================================================
// TEST BENCH (sirve a "test" y a "ab" testing)
// =============================================================================
//
// Layout: split horizontal 50/50.
//  - Izquierda : ficha origen del producto (datos crudos de la toma).
//  - Derecha   : output del LLM (streaming via NDJSON).
// En modo A/B la columna derecha muestra A vs B con un toggle.
//
// Streaming: usamos `streamTestBatch` (NDJSON) para que cada producto procesado
// aparezca apenas el LLM responde, en vez de esperar el batch completo.
// =============================================================================

interface ProductRow {
  id_catalogo: string | number;
  title: string;
  Marca?: string | null;
  Cat?: string | null;
  imagen?: string | null;
  catalog_brand?: string | null;
  url?: string | null;
  sitio?: string | null;
  tags?: string | unknown[] | null;
  [k: string]: any;
}

// Parsea product.tags ('[{id,tag:"KEY: Value"},...]') a un map { KEY: Value }.
// Resiliente a tags ausente, malformado o ya parseado. Devuelve {} si no hay nada.
function parseProductTags(tagsRaw: unknown): Record<string, string> {
  if (!tagsRaw) return {};
  let arr: any = tagsRaw;
  if (typeof tagsRaw === 'string') {
    try { arr = JSON.parse(tagsRaw); } catch { return {}; }
  }
  if (!Array.isArray(arr)) return {};
  const out: Record<string, string> = {};
  for (const entry of arr) {
    const tag = String((entry as any)?.tag ?? '');
    const idx = tag.indexOf(':');
    if (idx <= 0) continue;
    const k = tag.slice(0, idx).trim();
    const v = tag.slice(idx + 1).trim();
    if (k && v) out[k] = v;
  }
  return out;
}

// Devuelve los inputs efectivos que verá el prompt para un producto.
// Mismo orden que aparece en `<inputs>` del prompt v6.1+.
function getPromptInputs(product: ProductRow): {
  title: string;
  marca: string;
  cat: string;
  subcat: string;
  sitioCat: string;
  sitio: string;
} {
  const tags = parseProductTags(product.tags);
  return {
    title: product.title || '',
    marca: (product.Marca || '').toString(),
    cat: (product.Cat || '').toString(),
    subcat: tags.SUBCATEGORY || tags.SUBCATEGORIA || '',
    sitioCat: tags.SITE_CATEGORY || '',
    sitio: (product.sitio || '').toString(),
  };
}

// Status "vivo" por producto durante el run streaming.
type ItemStatus = 'pending' | 'running' | 'done' | 'error';

interface LiveItem {
  id: string;
  product: ProductRow;
  status: ItemStatus;
  resultA?: TestItemResult;
  resultB?: TestItemResult;
  // Edits locales (no persistidos a backend) — overrides sobre los campos del LLM.
  editsA?: Partial<{ catalog_brand: string; catalog_category: string; udm: string; contenido: string; segmento: string }>;
  editsB?: Partial<{ catalog_brand: string; catalog_category: string; udm: string; contenido: string; segmento: string }>;
}

const TestBenchSubtab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [tables, setTables] = useState<string[]>([]);
  const [tableName, setTableName] = useState<string>('');
  const [onlyUncataloged, setOnlyUncataloged] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  // Streaming state
  const [running, setRunning] = useState(false);
  const [items, setItems] = useState<LiveItem[]>([]);
  const [progress, setProgress] = useState({ a: 0, b: 0, total: 0 });
  const [latencies, setLatencies] = useState<number[]>([]); // para ETA
  const cancelRef = useRef<null | (() => void)>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [promptVersion, setPromptVersion] = useState<string | number | null>(null);

  // Historial de runs de test (para dropdown "Cargar run anterior")
  // viewMode='new': flujo normal de seleccionar+probar.
  // viewMode='loaded': estamos visualizando un run viejo (read-only).
  const [pastRuns, setPastRuns] = useState<RunDoc[]>([]);
  const [loadingPast, setLoadingPast] = useState(false);
  const [viewMode, setViewMode] = useState<'new' | 'loaded'>('new');
  const [loadedRunId, setLoadedRunId] = useState<string | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ path: string; sizeBytes: number; itemCount: number } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Display names del cliente (column-config admin)
  const { displayName, resolveAlias } = useColumnDisplayNames(clientId);

  useEffect(() => {
    api.get('/api/catalog/tables', { headers: { 'x-client-id': clientId } })
      .then(r => {
        const raw = (r.data as string[]) || [];
        const t = filterAndSortTomaTables(raw);
        setTables(t);
        if (t.length && !tableName) setTableName(t[0]);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [clientId]);

  // Cleanup al desmontar
  useEffect(() => () => { if (cancelRef.current) cancelRef.current(); }, []);

  // Cargar lista de runs previos del banco (runType='test') para el dropdown
  const reloadPastRuns = async () => {
    setLoadingPast(true);
    try {
      const { runs } = await listRuns(clientId, { limit: 50 });
      const tests = runs.filter(r => r.runType === 'test' || r.runType === 'manual_test');
      setPastRuns(tests);
    } catch {
      // silencio: dropdown vacío si falla
    } finally {
      setLoadingPast(false);
    }
  };
  useEffect(() => { reloadPastRuns(); /* eslint-disable-next-line */ }, [clientId]);

  // Cargar items de un run viejo y reconstruir LiveItems read-only
  const handleLoadPastRun = async (runId: string) => {
    if (!runId) return;
    setLoadingPast(true);
    setErrMsg(null);
    try {
      const [runMeta, itemsRes] = await Promise.all([
        getRun(clientId, runId),
        getRunItems(clientId, runId, { limit: 500 }),
      ]);
      // Side A vs B: el doc id es `<idCatalogo>__<side>`, agrupamos por idCatalogo.
      const byProduct = new Map<string, { product: ProductRow; A?: any; B?: any }>();
      for (const it of itemsRes.items || []) {
        const idStr = String(it.idCatalogo);
        if (!byProduct.has(idStr)) {
          byProduct.set(idStr, {
            product: {
              id_catalogo: it.idCatalogo,
              title: it.title || '',
              Marca: it.marcaOriginal || null,
              Cat: it.catOriginal || null,
              imagen: it.imagen || null,
            },
          });
        }
        const entry = byProduct.get(idStr)!;
        // Reconstruir TestItemResult shape para LLMOutputCard
        const reconstructed: TestItemResult = {
          id_catalogo: it.idCatalogo,
          title: it.title || '',
          imagen: it.imagen || null,
          marcaOriginal: it.marcaOriginal || null,
          catOriginal: it.catOriginal || null,
          final: {
            catalog_brand: it.finalBrand ?? null,
            catalog_category: it.finalCategory ?? null,
            confidence_score: it.confidence ?? null,
            segmento: it.finalSegmento ?? null,
            contenido: it.finalContenido ?? null,
            udm: it.finalUdm ?? null,
          },
          validation: it.validation || it.legacyValidation || undefined,
          tokenUsage: { input_tokens: it.tokensIn || 0, output_tokens: it.tokensOut || 0, total_tokens: (it.tokensIn || 0) + (it.tokensOut || 0), model: it.model || '' },
          costUsd: it.costUsd || 0,
          model: it.model || '',
          durationMs: it.durationMs || 0,
          error: it.error || undefined,
        };
        if (it.side === 'B') entry.B = reconstructed;
        else entry.A = reconstructed;
      }
      const liveItems: LiveItem[] = Array.from(byProduct.values()).map(({ product, A, B }) => ({
        id: String(product.id_catalogo),
        product,
        status: A?.error ? 'error' : 'done',
        resultA: A,
        resultB: B,
      }));
      setItems(liveItems);
      setProducts([]); // ocultar lista de selección
      setSelected(new Set());
      setProgress({ a: liveItems.filter(i => i.resultA).length, b: liveItems.filter(i => i.resultB).length, total: liveItems.length });
      setPromptVersion(runMeta.promptVersion ?? null);
      setViewMode('loaded');
      setLoadedRunId(runId);
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message || 'Error cargando run');
    } finally {
      setLoadingPast(false);
    }
  };

  // Volver al modo "new" — limpia items y deja al user cargar productos nuevos.
  const handleBackToNewRun = () => {
    setViewMode('new');
    setLoadedRunId(null);
    setItems([]);
    setProgress({ a: 0, b: 0, total: 0 });
    setLatencies([]);
    setErrMsg(null);
  };

  const loadProducts = async () => {
    if (!tableName) return;
    setLoading(true);
    setErrMsg(null);
    try {
      const poolLimit = pageSize * 5;
      const r = await api.get('/api/catalog/search-products', {
        headers: { 'x-client-id': clientId },
        params: {
          table: tableName,
          limit: poolLimit,
          offset: 0,
          onlyUncataloged: onlyUncataloged ? 'true' : 'false',
          // ORDER BY RAND() para que el pool tenga variedad de categorías.
          // El estratificado por Cat ocurre después en stratifiedSampleByCat.
          random: 'true',
        },
      });
      const pool: ProductRow[] = r.data.products || [];
      // Sampleo estratificado: garantiza ≥15% de cada categoría top (`Cat`)
      // presente en el pool. El resto al azar. Si el pool tiene 1 sola categoría
      // o menos productos que pageSize, equivale a shuffle puro.
      const randomized = stratifiedSampleByCat(pool, pageSize, 0.15);
      setProducts(randomized);
      setSelected(new Set(randomized.map(p => String(p.id_catalogo)))); // pre-seleccionar todos
      setItems([]);
      setProgress({ a: 0, b: 0, total: 0 });
      setLatencies([]);
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = () => {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map(p => String(p.id_catalogo))));
  };

  const toggleOne = (id: string | number) => {
    const k = String(id);
    const n = new Set(selected);
    if (n.has(k)) n.delete(k); else n.add(k);
    setSelected(n);
  };

  const handleRun = async () => {
    if (!tableName || selected.size === 0) return;
    setRunning(true);
    setErrMsg(null);
    setLatencies([]);

    // Inicializar items con productos seleccionados (todos en pending)
    const selectedProducts = products.filter(p => selected.has(String(p.id_catalogo)));
    const initialItems: LiveItem[] = selectedProducts.map(p => ({
      id: String(p.id_catalogo),
      product: p,
      status: 'pending',
    }));
    setItems(initialItems);
    setProgress({ a: 0, b: 0, total: selectedProducts.length });

    const productIds = selectedProducts.map(p => p.id_catalogo);
    const body: any = { productIds, tableName };

    try {
      const { cancel } = await streamTestBatch(clientId, body, (e: TestStreamEvent) => {
        if (e.type === 'start') {
          setProgress(prev => ({ ...prev, total: e.total }));
          setPromptVersion(e.promptVersion);
        } else if (e.type === 'result') {
          const idStr = String(e.item.id_catalogo);
          setItems(prev => prev.map(it => {
            if (it.id !== idStr) return it;
            const next: LiveItem = {
              ...it,
              status: e.item.error ? 'error' : 'done',
            };
            if (e.side === 'A') next.resultA = e.item;
            else next.resultB = e.item;
            return next;
          }));
          if (e.item.durationMs) setLatencies(prev => [...prev, e.item.durationMs!]);
          setProgress(prev => ({
            ...prev,
            a: e.side === 'A' ? prev.a + 1 : prev.a,
            b: e.side === 'B' ? prev.b + 1 : prev.b,
          }));
        } else if (e.type === 'error') {
          if (e.id_catalogo) {
            const idStr = String(e.id_catalogo);
            setItems(prev => prev.map(it => it.id === idStr ? { ...it, status: 'error' } : it));
          } else {
            setErrMsg(e.error);
          }
        } else if (e.type === 'done') {
          setRunning(false);
          cancelRef.current = null;
          // Refrescar dropdown de runs anteriores
          reloadPastRuns();
        }
      });
      cancelRef.current = cancel;
    } catch (e: any) {
      setErrMsg(e?.message || 'Error iniciando stream');
      setRunning(false);
    }
  };

  const handleCancelRun = () => {
    if (cancelRef.current) cancelRef.current();
    cancelRef.current = null;
    setRunning(false);
  };

  // ETA calc: promedio de latencias observadas * items pendientes
  const eta = useMemo(() => {
    if (!running || latencies.length === 0) return null;
    const avg = latencies.reduce((a, b) => a + b, 0) / latencies.length;
    const expectedTotal = progress.total;
    const completed = progress.a + progress.b;
    const remaining = Math.max(0, expectedTotal - completed);
    // concurrency 3 en backend; dividimos para estimar wall-time
    const concurrency = 3;
    const ms = (remaining / concurrency) * avg;
    return ms;
  }, [running, latencies, progress]);

  const updateItemEdit = (
    id: string,
    side: 'A' | 'B',
    field: 'catalog_brand' | 'catalog_category' | 'udm' | 'contenido' | 'segmento',
    value: string,
  ) => {
    setItems(prev => prev.map(it => {
      if (it.id !== id) return it;
      const editsKey = side === 'A' ? 'editsA' : 'editsB';
      return { ...it, [editsKey]: { ...(it[editsKey] || {}), [field]: value } };
    }));
  };

  // ¿Hay al menos 1 producto con resultado del LLM?
  const canExport = useMemo(
    () => items.some(it => it.resultA != null || it.resultB != null),
    [items],
  );

  const handleExport = async () => {
    if (!canExport || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const payload = {
        clientId,
        tableName,
        mode: 'test' as const,
        promptVersion,
        exportedAt: new Date().toISOString(),
        items: items.map(it => ({
          id: it.id,
          status: it.status,
          product: it.product,
          resultA: it.resultA || null,
          resultB: it.resultB || null,
          editsA: it.editsA || null,
          editsB: it.editsB || null,
        })),
      };
      const r = await api.post('/api/admin/banco-pruebas/export', payload, {
        headers: { 'x-client-id': clientId },
      });
      const { path, sizeBytes, itemCount } = r.data || {};
      if (!path) throw new Error('Backend no devolvió path');
      setExportResult({ path, sizeBytes: sizeBytes || 0, itemCount: itemCount || 0 });
      try { await navigator.clipboard.writeText(path); } catch {}
    } catch (e: any) {
      setExportError(e?.response?.data?.error || e.message || 'Error exportando');
    } finally {
      setExporting(false);
    }
  };

  const copyPathAgain = async () => {
    if (!exportResult) return;
    try { await navigator.clipboard.writeText(exportResult.path); } catch {}
  };

  // Métricas agregadas (basadas en items con resultado en el side activo)
  const metrics = useMemo(() => {
    const calc = (side: 'A' | 'B') => {
      const arr = items
        .map(it => side === 'A' ? it.resultA : it.resultB)
        .filter(Boolean) as TestItemResult[];
      if (arr.length === 0) return null;
      const total = arr.length;
      const matchOriginal = arr.filter(r => r.final?.catalog_brand && r.marcaOriginal &&
        String(r.final.catalog_brand).toLowerCase() === String(r.marcaOriginal).toLowerCase()).length;
      const passWhitelist = arr.filter(r => r.validation?.passedWhitelist).length;
      const avgLatency = arr.reduce((s, r) => s + (r.durationMs || 0), 0) / total;
      const totalCost = arr.reduce((s, r) => s + (r.costUsd || 0), 0);
      return { total, matchOriginal, passWhitelist, avgLatency, totalCost };
    };
    return { a: calc('A'), b: calc('B') };
  }, [items]);

  // ¿Mostramos el panel de resultados?
  const hasItems = items.length > 0;

  return (
    <div className="space-y-3">
      {/* Selector de runs anteriores del banco */}
      <div className="flex flex-wrap items-end gap-2 bg-amber-50/40 border border-amber-200 rounded-lg p-2">
        <div className="flex-1 min-w-[280px]">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Run anterior del banco{' '}
            <span className="text-gray-400 font-normal">({pastRuns.length} disponibles)</span>
          </label>
          <select
            value={loadedRunId || ''}
            disabled={loadingPast || running}
            onChange={e => {
              const v = e.target.value;
              if (v) handleLoadPastRun(v);
              else handleBackToNewRun();
            }}
            className="text-sm border border-gray-300 rounded px-2 py-1.5 w-full bg-white"
          >
            <option value="">— Run nuevo —</option>
            {pastRuns.map(r => {
              const dt = new Date(r.triggeredAt).toLocaleString('es-AR', { hour12: false });
              const sumario = `${dt} · ${tomaLabel(r.tableName || '')} · ${r.productsProcessed}/${r.productsTotal} · ${r.model?.name || ''}`;
              return <option key={r.runId} value={r.runId}>{sumario}</option>;
            })}
          </select>
        </div>
        {viewMode === 'loaded' && (
          <button
            onClick={handleBackToNewRun}
            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50"
            title="Volver al modo de armar un run nuevo"
          >
            <X className="w-3 h-3" /> Cerrar run
          </button>
        )}
      </div>

      {viewMode === 'loaded' && (
        <div className="text-xs bg-amber-100 border border-amber-300 rounded px-2 py-1.5 text-amber-800">
          Estás viendo un run anterior (read-only). Para procesar nuevos productos, seleccioná "— Run nuevo —" en el dropdown.
        </div>
      )}

      {/* Filtros */}
      <div className={`flex flex-wrap items-end gap-2 ${viewMode === 'loaded' ? 'opacity-50 pointer-events-none' : ''}`}>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Toma</label>
          <select value={tableName} onChange={e => setTableName(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1.5 min-w-[260px]">
            {tables.map(t => <option key={t} value={t}>{tomaLabel(t)}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Cantidad</label>
          <select value={pageSize} onChange={e => setPageSize(parseInt(e.target.value))}
                  className="text-sm border border-gray-300 rounded px-2 py-1.5">
            {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm h-[34px]">
          <input type="checkbox" checked={onlyUncataloged} onChange={e => setOnlyUncataloged(e.target.checked)} />
          Solo no catalogados
        </label>
        <button onClick={loadProducts} disabled={loading || !tableName || running}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Cargar
        </button>
        <button
          onClick={handleExport}
          disabled={!canExport || exporting || running}
          title={canExport ? 'Exporta los resultados actuales del banco a JSON en disco' : 'Corré el batch primero para tener resultados que exportar'}
          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Exportar resultados
        </button>

      </div>

      {/* Lista de productos disponibles + checkboxes de selección.
          Cada fila muestra los inputs efectivos que recibirá el prompt:
          title, Marca, Cat, Subcat, Sitio, SitioCat. Sirve como confirmación
          visual de qué campos se inyectan al LLM antes de correr el run. */}
      {products.length > 0 && !hasItems && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-2 py-1.5 bg-gray-50 border-b border-gray-200 text-xs text-gray-600 flex items-center justify-between">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={selected.size === products.length} onChange={toggleAll} />
              Seleccionar todo
            </label>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-gray-500 italic">Preview de inputs al prompt</span>
              <span className="tabular-nums">{selected.size} / {products.length}</span>
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                <tr className="text-[10px] uppercase tracking-wide text-gray-500">
                  <th className="px-2 py-1 w-6"></th>
                  <th className="px-2 py-1 w-10">Img</th>
                  <th className="px-2 py-1 text-left">Título</th>
                  <th className="px-2 py-1 text-left">Marca</th>
                  <th className="px-2 py-1 text-left">Cat</th>
                  <th className="px-2 py-1 text-left">Subcat</th>
                  <th className="px-2 py-1 text-left">Sitio</th>
                  <th className="px-2 py-1 text-left">Sitio Cat</th>
                </tr>
              </thead>
              <tbody>
                {products.map(p => {
                  const inputs = getPromptInputs(p);
                  const cell = (v: string) => v && v.trim() !== '' ? v : <span className="text-gray-300">—</span>;
                  return (
                    <tr key={String(p.id_catalogo)} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                      <td className="px-2 py-1.5 w-6">
                        <input type="checkbox" checked={selected.has(String(p.id_catalogo))} onChange={() => toggleOne(p.id_catalogo)} />
                      </td>
                      <td className="px-2 py-1.5 w-10">
                        {p.imagen ? <img src={p.imagen} alt="" className="w-8 h-8 object-contain" /> : <ImageIcon className="w-4 h-4 text-gray-300" />}
                      </td>
                      <td className="px-2 py-1.5 max-w-[260px]">
                        <span className="line-clamp-1" title={inputs.title}>{inputs.title}</span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-700">{cell(inputs.marca)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{cell(inputs.cat)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{cell(inputs.subcat)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{cell(inputs.sitio)}</td>
                      <td className="px-2 py-1.5 text-gray-700">{cell(inputs.sitioCat)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-2 py-1.5 bg-gray-50 border-t border-gray-200 flex items-center justify-end">
            <button onClick={handleRun} disabled={running || selected.size === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
              Probar con versión activa
            </button>
          </div>
        </div>
      )}

      {errMsg && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{errMsg}</div>}

      {/* Progress bar + ETA */}
      {hasItems && (
        <ProgressBar
          processed={progress.a + progress.b}
          total={progress.total}
          etaMs={eta}
          running={running}
          onCancel={running ? handleCancelRun : undefined}
        />
      )}

      {/* Métricas */}
      {hasItems && metrics.a && (
        <div className="grid gap-3 text-xs grid-cols-1">
          <MetricsCard title="Modelo activo" m={metrics.a} />
        </div>
      )}

      {/* Split 50/50 — origen vs LLM output */}
      {hasItems && (
        <div className="space-y-2">
          {items.map(it => (
            <SplitItemCard
              key={it.id}
              item={it}
              displayName={displayName}
              resolveAlias={resolveAlias}
              onEdit={updateItemEdit}
            />
          ))}
        </div>
      )}

      {/* CAMBIO #4: Recap collapsible al final del listado */}
      {hasItems && !running && <RunRecap items={items} clientId={clientId} loadedRunId={loadedRunId} /> }

      {/* Modal de export — error */}
      {exportError && !exportResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-lg w-full mx-4 p-4">
            <div className="text-base font-semibold text-red-700 mb-2">Error al exportar</div>
            <div className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{exportError}</div>
            <div className="flex justify-end">
              <button
                onClick={() => setExportError(null)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de export — éxito */}
      {exportResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-2xl w-full mx-4 p-4">
            <div className="text-base font-semibold text-gray-900 mb-1">Exportación lista</div>
            <div className="text-sm text-gray-600 mb-2">Path copiado al clipboard:</div>
            <div className="font-mono text-xs bg-gray-50 border border-gray-200 rounded p-2 break-all select-all mb-2">
              {exportResult.path}
            </div>
            <div className="text-[11px] text-gray-500 tabular-nums mb-3">
              {exportResult.itemCount} productos · {(exportResult.sizeBytes / 1024).toFixed(1)} KB
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={copyPathAgain}
                className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded"
              >
                Copiar de nuevo
              </button>
              <button
                onClick={() => setExportResult(null)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Subcomponentes
// -----------------------------------------------------------------------------

const ProgressBar: React.FC<{
  processed: number;
  total: number;
  etaMs: number | null;
  running: boolean;
  onCancel?: () => void;
}> = ({ processed, total, etaMs, running, onCancel }) => {
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const etaText = etaMs != null && running ? formatEta(etaMs) : null;
  return (
    <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
      <div className="flex items-center justify-between text-xs mb-1.5 tabular-nums">
        <span className="text-gray-700">
          <span className="font-semibold">{processed}</span> de <span className="font-semibold">{total}</span> procesados
          {' '}<span className="text-gray-400">({pct}%)</span>
        </span>
        <div className="flex items-center gap-2">
          {etaText && <span className="text-gray-500 text-[11px]">~{etaText} restantes</span>}
          {running && onCancel && (
            <button onClick={onCancel} className="flex items-center gap-1 px-2 py-0.5 text-[11px] border border-gray-300 rounded hover:bg-white">
              <X className="w-3 h-3" /> Cancelar
            </button>
          )}
        </div>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        {running && total === 0
          ? <div className="h-full bg-blue-500 animate-pulse w-1/3" />
          : <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />}
      </div>
    </div>
  );
};

function formatEta(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}m ${s}s`;
}

/**
 * Formatea ms a "MM:SS" o "HH:MM:SS" si > 1h.
 * Devuelve "0:00" si ms <= 0 o NaN.
 */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

/**
 * Formatea velocidad como "X prod/s (Y/min)".
 * Si <1 prod/s, muestra solo prod/min.
 */
function formatSpeed(prodPerSec: number): string {
  if (!Number.isFinite(prodPerSec) || prodPerSec <= 0) return '—';
  const perMin = Math.round(prodPerSec * 60);
  if (prodPerSec < 1) return `${perMin}/min`;
  return `${prodPerSec.toFixed(1)} prod/s (${perMin}/min)`;
}

const SplitItemCard: React.FC<{
  item: LiveItem;
  displayName: (f: string) => string;
  resolveAlias: (aliases: string[]) => string | null;
  onEdit: (
    id: string,
    side: 'A' | 'B',
    field: 'catalog_brand' | 'catalog_category' | 'udm' | 'contenido' | 'segmento',
    value: string,
  ) => void;
}> = ({ item, displayName, resolveAlias, onEdit }) => {
  const activeSide: 'A' | 'B' = 'A';
  const result = item.resultA;
  const edits = item.editsA || {};

  return (
    <div className="border border-gray-200 bg-white rounded-lg overflow-hidden">
      <div className="grid grid-cols-2 divide-x divide-gray-200">
        {/* IZQUIERDA: ficha origen */}
        <ProductOriginCard product={item.product} displayName={displayName} resolveAlias={resolveAlias} />

        {/* DERECHA: output del LLM */}
        <LLMOutputCard
          item={item}
          activeSide={activeSide}
          result={result}
          edits={edits}
          onEdit={onEdit}
        />
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Ficha izquierda — origen
// -----------------------------------------------------------------------------

const ProductOriginCard: React.FC<{
  product: ProductRow;
  displayName: (f: string) => string;
  resolveAlias: (aliases: string[]) => string | null;
}> = ({ product, displayName, resolveAlias }) => {
  const [imgOpen, setImgOpen] = useState(false);

  // Resolver campos posibles (el cliente puede tenerlos con distintos nombres)
  const udmKey = resolveAlias(['udm', 'UDM', 'inches']) || 'inches';
  const contenidoKey = resolveAlias(['contenido', 'CONTENIDO', 'CONTENIDO_TOTAL']) || 'contenido';
  const segmentoKey = resolveAlias(['segmento', 'SEGMENTO']) || 'segmento';
  const sitioCatKey = resolveAlias(['SITIO_CAT', 'sitio_cat', 'siteCategory']) || 'SITIO_CAT';
  const subCatKey = resolveAlias(['Subcat', 'subcat', 'SUBCAT', 'subcategoria']) || 'Subcat';

  const labels = {
    sitio: displayName('sitio'),
    title: displayName('title'),
    cat: displayName('Cat'),
    subcat: displayName(subCatKey),
    marca: displayName('Marca'),
    udm: displayName(udmKey),
    contenido: displayName(contenidoKey),
    segmento: displayName(segmentoKey),
    sitioCat: displayName(sitioCatKey),
  };

  // SUBCATEGORY/SITE_CATEGORY/CATEGORY no viven como columnas top-level del producto,
  // sino dentro del JSON `tags`. Resolvemos al vuelo (helper compartido a nivel módulo)
  // y dejamos que `val()` los use como fallback cuando product[key] no existe.
  const tagMap = useMemo(() => parseProductTags((product as any).tags), [(product as any).tags]);

  const fromTags = (key: string): string | null => {
    if (!key) return null;
    const upper = key.toUpperCase();
    // Aliases conocidos: 'Subcat' / 'subcat' → SUBCATEGORY ; 'SITIO_CAT' → SITE_CATEGORY
    if (upper === 'SUBCAT' || upper === 'SUBCATEGORIA') return tagMap.SUBCATEGORY || tagMap.SUBCATEGORIA || null;
    if (upper === 'SITIO_CAT' || upper === 'SITE_CATEGORY' || upper === 'SITECATEGORY') return tagMap.SITE_CATEGORY || null;
    return tagMap[upper] || null;
  };

  const val = (key: string): string => {
    const v = (product as any)[key];
    if (v != null && v !== '') return String(v);
    const fromTag = fromTags(key);
    if (fromTag != null && fromTag !== '') return fromTag;
    return '—';
  };

  return (
    <>
      <div className="p-3 space-y-2">
        <div className="flex items-start gap-3">
          {/* Thumb */}
          {product.imagen ? (
            <button
              onClick={() => setImgOpen(true)}
              className="shrink-0 w-20 h-20 border border-gray-200 rounded overflow-hidden bg-white hover:ring-2 hover:ring-purple-300"
              title="Click para ampliar"
            >
              <img src={product.imagen} alt="" className="w-full h-full object-contain" />
            </button>
          ) : (
            <div className="shrink-0 w-20 h-20 border border-gray-200 rounded bg-gray-50 flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-gray-300" />
            </div>
          )}

          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-start justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{labels.sitio}: {val('sitio')}</span>
              {product.url && (
                <a href={product.url} target="_blank" rel="noreferrer"
                   className="shrink-0 text-gray-400 hover:text-purple-600"
                   title="Abrir en sitio">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            <div className="text-sm text-gray-900 leading-snug line-clamp-3" title={product.title}>
              {product.title || '—'}
            </div>
            <div className="font-mono text-[10px] text-gray-400">ID {product.id_catalogo}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs pt-1 border-t border-gray-100">
          <FieldRow label={labels.cat} value={val('Cat')} />
          <FieldRow label={labels.subcat} value={val(subCatKey)} />
          <FieldRow label={labels.marca} value={val('Marca')} />
          <FieldRow label={labels.udm} value={val(udmKey)} />
          <FieldRow label={labels.contenido} value={val(contenidoKey)} />
          <FieldRow label={labels.segmento} value={val(segmentoKey)} />
          <FieldRow label={labels.sitioCat} value={val(sitioCatKey)} className="col-span-2" />
        </div>
      </div>

      {imgOpen && (
        <ImageModal
          isOpen={imgOpen}
          onClose={() => setImgOpen(false)}
          imageUrl={product.imagen || ''}
          productTitle={product.title}
          productUrl={product.url || undefined}
          siteName={product.sitio || undefined}
        />
      )}
    </>
  );
};

const FieldRow: React.FC<{ label: string; value: string; className?: string }> = ({ label, value, className }) => (
  <div className={`flex flex-col gap-0.5 ${className || ''}`}>
    <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{label}</span>
    <span className="text-gray-800 truncate" title={value}>{value}</span>
  </div>
);

// -----------------------------------------------------------------------------
// Output derecha — LLM
// -----------------------------------------------------------------------------

const LLMOutputCard: React.FC<{
  item: LiveItem;
  activeSide: 'A' | 'B';
  result: TestItemResult | undefined;
  edits: Partial<{ catalog_brand: string; catalog_category: string; udm: string; contenido: string; segmento: string }>;
  onEdit: (
    id: string,
    side: 'A' | 'B',
    field: 'catalog_brand' | 'catalog_category' | 'udm' | 'contenido' | 'segmento',
    value: string,
  ) => void;
}> = ({ item, activeSide, result, edits, onEdit }) => {
  // Estado vacío / cargando
  if (!result) {
    return (
      <div className="p-3 flex items-center justify-center min-h-[180px] text-xs text-gray-400">
        {item.status === 'pending' && (
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
            En cola...
          </div>
        )}
        {item.status === 'running' && (
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
            Procesando con LLM...
          </div>
        )}
        {item.status === 'error' && (
          <div className="text-red-500">Sin resultado</div>
        )}
      </div>
    );
  }

  if (result.error) {
    return (
      <div className="p-3 min-h-[180px] text-xs">
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2">
          Error LLM: {result.error}
        </div>
      </div>
    );
  }

  const final = result.final || {};
  const conf = final.confidence_score ?? null;
  const confColor =
    conf == null ? 'bg-gray-200 text-gray-600' :
    conf >= 0.85 ? 'bg-emerald-100 text-emerald-700' :
    conf >= 0.7  ? 'bg-amber-100 text-amber-700' :
                   'bg-red-100 text-red-700';

  // Brand match indicator
  const brandLLM = String(final.catalog_brand || '').toLowerCase();
  const brandOrig = String(result.marcaOriginal || '').toLowerCase();
  const brandMatch = brandLLM && brandOrig && brandLLM === brandOrig;

  // CAMBIO #4: indicadores derivados de validation V2
  const v: any = result.validation || {};
  const flagFromLlm = v.from_llm === true && v.not_whitelisted === true;
  const flagDisagreement = v.disagreement === true;
  const flagTomaNotWl = v.toma_not_whitelisted === true;
  const flagSubcatNotInMatrix = v.subcategory_not_in_matrix === true;
  const subcatSugg = v.subcategory_suggested || null;
  const disagreementValue = v.disagreement_value || null;

  const fieldVal = (k: 'catalog_brand' | 'catalog_category' | 'udm' | 'contenido' | 'segmento'): string => {
    if (edits[k] !== undefined) return String(edits[k]);
    return final[k] != null ? String(final[k]) : '';
  };

  return (
    <div className="p-3 space-y-2">
      {/* Header: confidence + latency + model. Banco de pruebas es read-only;
          la curaduría manual vive en la grilla de Publicaciones (click-to-edit + PATCH). */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${confColor}`}>
          {conf != null ? `Conf ${conf.toFixed(2)}` : 'Sin score'}
        </span>
        {result.durationMs != null && (
          <span className="text-[10px] text-gray-500 tabular-nums">{result.durationMs} ms</span>
        )}
        {result.model && (
          <span className="text-[10px] text-gray-400 truncate max-w-[140px]" title={result.model}>{result.model}</span>
        )}
        {mode_indicator(activeSide)}
      </div>

      {/* Campos editables */}
      <div className="grid grid-cols-2 gap-2">
        <EditableField
          label="Marca"
          value={fieldVal('catalog_brand')}
          onChange={(v) => onEdit(item.id, activeSide, 'catalog_brand', v)}
          extra={
            <span className="inline-flex items-center gap-1">
              {brandMatch && <Check className="w-3 h-3 text-emerald-600 inline" />}
              {!brandMatch && brandOrig && !flagDisagreement && <X className="w-3 h-3 text-amber-500 inline" />}
              {/* CAMBIO #4: ⚠ amber — LLM extrajo, fuera de whitelist */}
              {flagFromLlm && (
                <span title="Marca extraída por LLM, fuera de whitelist"
                      className="text-amber-600 text-[11px] font-bold">⚠</span>
              )}
              {/* CAMBIO #4: 🟡 — discrepancia toma vs LLM */}
              {flagDisagreement && (
                <span title={`Toma dijo "${result.marcaOriginal}", LLM dijo "${disagreementValue}"`}
                      className="text-yellow-500 text-[11px]">●</span>
              )}
              {/* CAMBIO #4: 🟡 — toma no en whitelist actual */}
              {flagTomaNotWl && (
                <span title="Marca de toma no está en whitelist actual"
                      className="text-yellow-600 text-[11px]">○</span>
              )}
            </span>
          }
        />
        <EditableField
          label="Categoría"
          value={fieldVal('catalog_category')}
          onChange={(v) => onEdit(item.id, activeSide, 'catalog_category', v)}
          extra={
            flagSubcatNotInMatrix && subcatSugg ? (
              <span title={`LLM sugirió "${subcatSugg}", no en matriz`}
                    className="text-amber-600 text-[11px] font-bold">⚠</span>
            ) : null
          }
        />
        <EditableField
          label="UDM"
          value={fieldVal('udm')}
          onChange={(v) => onEdit(item.id, activeSide, 'udm', v)}
        />
        <EditableField
          label="Contenido"
          value={fieldVal('contenido')}
          onChange={(v) => onEdit(item.id, activeSide, 'contenido', v)}
        />
        <EditableField
          label="Segmento"
          value={fieldVal('segmento')}
          onChange={(v) => onEdit(item.id, activeSide, 'segmento', v)}
          className="col-span-2"
        />
      </div>

      {/* Validation warnings */}
      {result.validation && (result.validation.hallucinationsDetected || result.validation.belowThreshold || result.validation.categoryNotLeaf) && (
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-1.5">
          {result.validation.hallucinationsDetected && <div>⚠ Hallucination detectada</div>}
          {result.validation.belowThreshold && <div>⚠ Confidence bajo umbral</div>}
          {result.validation.categoryNotLeaf && <div>⚠ Categoría no es leaf</div>}
        </div>
      )}

      {/* Reasoning — siempre visible (evidencia del LLM) */}
      {final.reasoning && (
        <div className="border-t border-gray-100 pt-1.5">
          <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1">
            Reasoning
          </div>
          <div className="text-[11px] text-gray-600 italic bg-gray-50 rounded p-2 leading-snug whitespace-pre-wrap">
            {final.reasoning}
          </div>
        </div>
      )}
    </div>
  );
};

function mode_indicator(side: 'A' | 'B') {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${
      side === 'A' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
    }`}>{side}</span>
  );
}

// -----------------------------------------------------------------------------
// EditableField — input inline con toggle al hacer click
// -----------------------------------------------------------------------------

const EditableField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  extra?: React.ReactNode;
}> = ({ label, value, onChange, className, extra }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  return (
    <div className={`flex flex-col gap-0.5 ${className || ''}`}>
      <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{label}</span>
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') { setEditing(false); setDraft(value); }
          }}
          className="text-xs border border-purple-300 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-400"
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="group text-left text-xs text-gray-800 hover:text-purple-700 flex items-center gap-1 truncate"
          title={value || 'Click para editar'}
        >
          <span className={`truncate ${value ? '' : 'text-gray-400 italic'}`}>{value || '—'}</span>
          {extra}
          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 shrink-0" />
        </button>
      )}
    </div>
  );
};

// CAMBIO #4: Recap collapsible — counts por categoría
const RunRecap: React.FC<{ items: LiveItem[]; clientId: string; loadedRunId: string | null }> = ({ items, clientId, loadedRunId }) => {
  const [open, setOpen] = useState(false);

  const summary = useMemo(() => {
    let newBrands = 0; // from_llm + not_whitelisted
    let disagreements = 0;
    let subcatsSugg = 0;
    let tomaNotWl = 0;
    const newBrandsMap = new Map<string, number>();
    const subcatsMap = new Map<string, number>();
    const disagreementsMap = new Map<string, number>();

    for (const it of items) {
      const r = it.resultA;
      if (!r) continue;
      const v: any = r.validation || {};
      if (v.from_llm === true && v.not_whitelisted === true) {
        newBrands++;
        const k = String(r.final?.catalog_brand || '');
        if (k) newBrandsMap.set(k, (newBrandsMap.get(k) || 0) + 1);
      }
      if (v.disagreement === true) {
        disagreements++;
        const k = `${r.marcaOriginal || ''} → ${v.disagreement_value || ''}`;
        disagreementsMap.set(k, (disagreementsMap.get(k) || 0) + 1);
      }
      if (v.subcategory_not_in_matrix === true && v.subcategory_suggested) {
        subcatsSugg++;
        const k = String(v.subcategory_suggested);
        subcatsMap.set(k, (subcatsMap.get(k) || 0) + 1);
      }
      if (v.toma_not_whitelisted === true) tomaNotWl++;
    }
    return {
      newBrands, disagreements, subcatsSugg, tomaNotWl,
      newBrandsMap, subcatsMap, disagreementsMap,
    };
  }, [items]);

  const total = summary.newBrands + summary.disagreements + summary.subcatsSugg + summary.tomaNotWl;
  if (total === 0) return null;

  return (
    <div className="border border-purple-200 bg-purple-50/40 rounded-lg">
      <button onClick={() => setOpen(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-purple-50">
        <div className="flex items-center gap-2 text-sm">
          <FileText className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-gray-800">Resumen del run</span>
          <span className="text-xs text-gray-500 tabular-nums">
            ({summary.newBrands} nuevas · {summary.disagreements} discrepancias · {summary.subcatsSugg} subcats · {summary.tomaNotWl} toma no whitelist)
          </span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 text-xs">
          <RecapSection title={`Marcas nuevas (${summary.newBrands})`} entries={summary.newBrandsMap} />
          <RecapSection title={`Discrepancias (${summary.disagreements})`} entries={summary.disagreementsMap} />
          <RecapSection title={`Subcats sugeridas (${summary.subcatsSugg})`} entries={summary.subcatsMap} />
          {summary.tomaNotWl > 0 && (
            <div className="text-gray-600">
              Marcas de toma no whitelist: <span className="font-semibold">{summary.tomaNotWl}</span>
            </div>
          )}
          {loadedRunId && (
            <div className="pt-2 border-t border-purple-100">
              <a href="#" onClick={(e) => { e.preventDefault(); window.location.hash = `report-${loadedRunId}`; }}
                 className="text-purple-600 hover:underline">
                Ver reporte completo →
              </a>
              <span className="ml-2 text-[10px] text-gray-400">(en sub-tab Reportes, runId {loadedRunId.slice(0, 8)})</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const RecapSection: React.FC<{ title: string; entries: Map<string, number> }> = ({ title, entries }) => {
  if (entries.size === 0) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold text-gray-700 uppercase tracking-wide mb-1">{title}</div>
      <div className="flex flex-wrap gap-1">
        {Array.from(entries.entries()).sort((a, b) => b[1] - a[1]).map(([k, c]) => (
          <span key={k} className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[11px]">
            {k} <span className="text-gray-400 tabular-nums">×{c}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const MetricsCard: React.FC<{ title: string; m: any }> = ({ title, m }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
    <div className="text-xs font-semibold text-gray-700 mb-1.5">{title}</div>
    <div className="space-y-0.5 text-xs tabular-nums text-gray-600">
      <div>Total: <span className="font-semibold text-gray-900">{m.total}</span></div>
      <div>Match marca origen: <span className="font-semibold text-gray-900">{m.matchOriginal}/{m.total} ({((m.matchOriginal/m.total)*100).toFixed(0)}%)</span></div>
      <div>Pass whitelist: <span className="font-semibold text-gray-900">{m.passWhitelist}/{m.total}</span></div>
      <div>Latencia avg: <span className="font-semibold text-gray-900">{m.avgLatency.toFixed(0)} ms</span></div>
      <div>Costo total: <span className="font-semibold text-gray-900">${m.totalCost.toFixed(4)}</span></div>
    </div>
  </div>
);

// =============================================================================
// HISTORY
// =============================================================================

type RunTypeFilter = 'all' | 'test' | 'batch';

// Heurística zombi: run en 'processing' que claramente no avanzó.
// Sin un campo 'lastUpdate' en el RunDoc, usamos triggeredAt + counters como
// proxy. Un batch grande puede tardar >10 min legítimamente — sólo marcamos
// zombi cuando hay evidencia clara de que el worker nunca arrancó (productsTotal
// no se inicializó) o quedó frozen en 0 procesados después de un tiempo largo.
const ZOMBIE_THRESHOLD_MS = 10 * 60 * 1000; // 10 min (worker nunca arrancó)
const ZOMBIE_STALLED_MS = 30 * 60 * 1000;   // 30 min (worker inicializó pero 0 procesados)

function isZombieRun(r: RunDoc): boolean {
  if (r.status !== 'processing') return false;
  if (!r.triggeredAt) return false;
  const t = Date.parse(r.triggeredAt);
  if (Number.isNaN(t)) return false;
  const ageMs = Date.now() - t;
  if (ageMs <= ZOMBIE_THRESHOLD_MS) return false;
  // Worker vivo: ya procesó al menos 1 item.
  if ((r.productsProcessed || 0) > 0) return false;
  // Worker nunca arrancó: productsTotal sigue en 0 después de >10 min.
  if ((r.productsTotal || 0) === 0) return true;
  // Worker inicializó (productsTotal>0) pero lleva >30 min sin procesar nada.
  return ageMs > ZOMBIE_STALLED_MS;
}

const HistorySubtab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [runs, setRuns] = useState<RunDoc[]>([]);
  const [loading, setLoading] = useState(true);
  // Run abierto en el modal inspector (full-screen). null = modal cerrado.
  const [selected, setSelected] = useState<RunDoc | null>(null);
  // Run pendiente de confirmación para "marcar cancelado" (modal de confirm).
  const [zombieConfirm, setZombieConfirm] = useState<RunDoc | null>(null);
  const [markingCancelled, setMarkingCancelled] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<RunTypeFilter>('all');
  // Track del último runId copiado al clipboard (para feedback visual 1.5s).
  const [copiedRunId, setCopiedRunId] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { runs } = await listRuns(clientId, { limit: 100 });
      setRuns(runs);
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const openRun = (r: RunDoc) => {
    // Abre el Inspector full-screen (mismo componente y UX que el botón
    // "Inspeccionar ítems" del subtab Batch durante un run activo).
    setSelected(r);
  };

  const confirmMarkCancelled = async () => {
    if (!zombieConfirm) return;
    setMarkingCancelled(true);
    try {
      await markRunCancelled(clientId, zombieConfirm.runId, 'zombie marcado desde histórico');
      setZombieConfirm(null);
      await reload();
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setMarkingCancelled(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [clientId]);

  // Filtrado client-side: 'test' incluye legacy 'manual_test'; 'batch' incluye 'recatalog'.
  const filteredRuns = useMemo(() => {
    if (typeFilter === 'all') return runs;
    if (typeFilter === 'test') return runs.filter(r => r.runType === 'test' || r.runType === 'manual_test');
    return runs.filter(r => r.runType === 'batch' || r.runType === 'recatalog');
  }, [runs, typeFilter]);

  const counts = useMemo(() => ({
    all: runs.length,
    test: runs.filter(r => r.runType === 'test' || r.runType === 'manual_test').length,
    batch: runs.filter(r => r.runType === 'batch' || r.runType === 'recatalog').length,
  }), [runs]);

  if (loading) return <div className="text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" />Cargando...</div>;

  const renderTypeBadge = (rt: RunDoc['runType']) => {
    const isTest = rt === 'test' || rt === 'manual_test';
    const cls = isTest
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700';
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>{rt}</span>;
  };

  return (
    <div className="space-y-3">
      <BrandSuggestionsPanel clientId={clientId} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* Filtros tipo run */}
        <div className="flex items-center gap-1 text-xs">
          {([
            { id: 'all',   label: 'Todos',           count: counts.all },
            { id: 'test',  label: 'Banco de Pruebas', count: counts.test },
            { id: 'batch', label: 'Batch real',       count: counts.batch },
          ] as { id: RunTypeFilter; label: string; count: number }[]).map(opt => {
            const active = typeFilter === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setTypeFilter(opt.id)}
                className={`px-2 py-1 rounded border ${active
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >
                {opt.label} <span className="tabular-nums opacity-80">({opt.count})</span>
              </button>
            );
          })}
        </div>
        <button onClick={reload} className="flex items-center gap-1.5 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">
          <RefreshCw className="w-3 h-3" /> Refrescar
        </button>
      </div>

      {errMsg && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{errMsg}</div>}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-2 py-1.5 text-left">Fecha</th>
              <th className="px-2 py-1.5 text-left">Tipo</th>
              <th className="px-2 py-1.5 text-left">Status</th>
              <th className="px-2 py-1.5 text-left">Modelo</th>
              <th className="px-2 py-1.5 text-right">Procesados</th>
              <th className="px-2 py-1.5 text-right">Errores</th>
              <th className="px-2 py-1.5 text-left">Run ID</th>
              <th className="px-2 py-1.5 text-right">Costo</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {filteredRuns.map(r => {
              const zombie = isZombieRun(r);
              return (
                <tr key={r.runId} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                    onClick={() => openRun(r)}>
                  <td className="px-2 py-1.5 tabular-nums">{new Date(r.triggeredAt).toLocaleString('es-AR', { hour12: false })}</td>
                  <td className="px-2 py-1.5">{renderTypeBadge(r.runType)}</td>
                  <td className="px-2 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      r.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      r.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                      r.status === 'cancelled' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{r.status}</span>
                    {zombie && (
                      <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700"
                            title="Run en processing sin actividad reciente (>10 min) — probablemente zombi">
                        zombi
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{r.model?.provider} · {r.model?.name}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.productsProcessed}/{r.productsTotal}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.productsFailed}</td>
                  <td className="px-2 py-1.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        try {
                          navigator.clipboard.writeText(r.runId);
                          setCopiedRunId(r.runId);
                          setTimeout(() => setCopiedRunId(curr => curr === r.runId ? null : curr), 1500);
                        } catch {/* ignore */}
                      }}
                      title="Click para copiar run ID al clipboard"
                      className="font-mono text-[10px] text-gray-600 hover:text-blue-700 hover:underline tabular-nums inline-flex items-center gap-1"
                    >
                      {copiedRunId === r.runId ? (
                        <><Check className="w-3 h-3 text-emerald-600" /> copiado</>
                      ) : (
                        <><Copy className="w-3 h-3 opacity-50" /> {r.runId.slice(0, 8)}…</>
                      )}
                    </button>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">${(r.costUsd || 0).toFixed(4)}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center justify-end gap-1">
                      {zombie && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setZombieConfirm(r); }}
                          title="Marcar este run zombi como cancelado"
                          className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] border border-orange-300 text-orange-700 hover:bg-orange-50 rounded"
                        >
                          <Ban className="w-3 h-3" /> Marcar cancelado
                        </button>
                      )}
                      <ChevronRight className="w-3 h-3 text-gray-400" />
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredRuns.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-4 text-center text-gray-400">Sin runs para este filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Inspector full-screen: mismo componente y UX que el botón
          "Inspeccionar ítems" del subtab Batch durante un run activo. */}
      {selected && (
        <InspectItemsModal
          clientId={clientId}
          run={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Modal de confirmación "Marcar cancelado" para runs zombi.
          Diseño propio (NO window.confirm) — backdrop oscuro + dialog centrado +
          icono warning + botones "Volver" / "Sí, marcar cancelado". */}
      {zombieConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-orange-200 max-w-md w-full mx-4 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <h4 className="text-base font-semibold text-orange-700">Marcar run como cancelado</h4>
            </div>
            <div className="text-sm text-gray-700 mb-3 space-y-1">
              <p>
                Este run está en <code className="bg-gray-100 px-1 rounded">processing</code>{' '}
                desde <span className="font-medium">{new Date(zombieConfirm.triggeredAt).toLocaleString('es-AR', { hour12: false })}</span>{' '}
                (más de 10 minutos sin actividad reciente).
              </p>
              <p>
                Probablemente el runner murió. Marcarlo como{' '}
                <code className="bg-gray-100 px-1 rounded">cancelled</code> escribe directamente
                el doc en Firestore (no depende del runner). Quedará trazabilidad:{' '}
                <code className="bg-gray-100 px-1 rounded">cancelReason: 'zombie marcado desde histórico'</code>.
              </p>
              <p className="text-xs text-gray-500">
                Run: <span className="font-mono">{zombieConfirm.runId.slice(0, 8)}</span>
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setZombieConfirm(null)}
                disabled={markingCancelled}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Volver
              </button>
              <button
                onClick={confirmMarkCancelled}
                disabled={markingCancelled}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded disabled:bg-gray-300"
              >
                {markingCancelled ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                Sí, marcar cancelado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// BRAND SUGGESTIONS — marcas detectadas por el LLM que NO están en whitelist
// =============================================================================

const BrandSuggestionsPanel: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [suggestions, setSuggestions] = useState<BrandSuggestion[]>([]);
  const [activePromptVersion, setActivePromptVersion] = useState<string | number | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [s, pp] = await Promise.all([
        listBrandSuggestions(clientId, { limit: 100 }),
        listPrompts(clientId),
      ]);
      setSuggestions(s.suggestions || []);
      const active = (pp.prompts || []).find(p => p.isActive);
      setActivePromptVersion(active?.version ?? null);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [clientId]);

  const addToWhitelist = async (s: BrandSuggestion) => {
    if (activePromptVersion == null) {
      alert('No hay versión activa para agregar al BRANDS.');
      return;
    }
    try {
      await patchPromptVariable(clientId, activePromptVersion, 'BRANDS', {
        addValues: [s.suggestedBrand],
      });
      // Marcar como ignorada (ya quedó incorporada)
      await patchBrandSuggestion(clientId, s.id, { ignored: true });
      await reload();
    } catch (e: any) {
      alert('Error: ' + (e?.response?.data?.error || e.message));
    }
  };

  const ignore = async (s: BrandSuggestion) => {
    try {
      await patchBrandSuggestion(clientId, s.id, { ignored: true });
      await reload();
    } catch (e: any) {
      alert('Error: ' + (e?.response?.data?.error || e.message));
    }
  };

  const total = suggestions.length;
  if (!loading && total === 0 && !err) return null;

  return (
    <div className="border border-amber-200 bg-amber-50/40 rounded-lg">
      <button onClick={() => setExpanded(v => !v)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-amber-50">
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-gray-800">Marcas sugeridas</span>
          <span className="text-xs text-gray-500 tabular-nums">({total})</span>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
        </div>
        <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {err && <div className="px-3 pb-2 text-xs text-red-700">{err}</div>}
      {expanded && total > 0 && (
        <div className="px-3 pb-3 max-h-[360px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-amber-100/50 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left">Marca sugerida</th>
                <th className="px-2 py-1 text-right">Count</th>
                <th className="px-2 py-1 text-left">Ejemplos</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map(s => (
                <tr key={s.id} className="border-b border-amber-100 last:border-b-0">
                  <td className="px-2 py-1 font-semibold">{s.suggestedBrand}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{s.count}</td>
                  <td className="px-2 py-1 text-gray-600">
                    <div className="space-y-0.5">
                      {(s.exampleTitles || []).slice(0, 3).map((t, i) => (
                        <div key={i} className="truncate max-w-[400px]" title={t}>{t}</div>
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    <button onClick={() => addToWhitelist(s)}
                            disabled={activePromptVersion == null}
                            className="px-2 py-0.5 text-[11px] bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded mr-1">
                      + Whitelist
                    </button>
                    <button onClick={() => ignore(s)}
                            className="px-2 py-0.5 text-[11px] border border-gray-300 hover:bg-gray-100 rounded">
                      Ignorar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {activePromptVersion == null && (
            <div className="mt-2 text-[11px] text-amber-700">
              No hay versión activa del prompt — el botón "+ Whitelist" está deshabilitado.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// INSPECT ITEMS MODAL — abre desde BatchSubtab para inspeccionar items de un run
// =============================================================================

type InspectItem = Record<string, any>;

type InspectFilterKey =
  | 'all'
  | 'success'
  | 'error'
  | 'disagreement'
  | 'hallucination'
  | 'belowThreshold'
  | 'forcedToOtros'
  | 'subcatNotInMatrix';

const INSPECT_FILTERS: { key: InspectFilterKey; label: string }[] = [
  { key: 'all',                label: 'Todos' },
  { key: 'success',            label: 'OK' },
  { key: 'error',              label: 'Errores' },
  { key: 'disagreement',       label: 'Brand disagreement' },
  { key: 'hallucination',      label: 'Hallucinations' },
  { key: 'belowThreshold',     label: 'Below threshold' },
  { key: 'forcedToOtros',      label: 'Segment → Otros' },
  { key: 'subcatNotInMatrix',  label: 'Subcat off-matrix' },
];

function matchInspectFilter(it: InspectItem, key: InspectFilterKey): boolean {
  const v2 = it.validationV2 || {};
  const v1 = it.validationResult || {};
  switch (key) {
    case 'all': return true;
    case 'success': return !it.error;
    case 'error': return !!it.error;
    case 'disagreement': return v2.disagreement === true;
    case 'hallucination':
      return v2.hallucinationsDetected === true
        || v2.not_whitelisted === true
        || v1.hallucinationsDetected === true;
    case 'belowThreshold': return v2.belowThreshold === true || v1.belowThreshold === true;
    case 'forcedToOtros': return v2.forcedToOtros === true || v2.forced_to_otros === true;
    case 'subcatNotInMatrix': return v2.subcategory_not_in_matrix === true;
    default: return true;
  }
}

/**
 * Parsea el raw LLM response (string JSON o ya-objeto) de forma resiliente.
 * Maneja: objeto directo, JSON puro, JSON envuelto en ```json...```, JSON con
 * texto envolvente. Devuelve {} si nada matchea — no lanza.
 *
 * Usado como fuente de fallback cuando el itemDoc de Firestore no persistió
 * algunos campos (runs viejos, antes del 2026-05-07): reasoning, subcategory,
 * etc. Política: lo persistido gana; si falta, derivamos del raw.
 */
function parseRawLLMResponse(raw: unknown): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === 'object') return raw as Record<string, any>;
  if (typeof raw !== 'string') return {};
  try { return JSON.parse(raw); } catch (_) { /* try fallbacks */ }
  const codeBlock = raw.match(/```(?:json)?\s*([\s\S]+?)\s*```/);
  if (codeBlock) { try { return JSON.parse(codeBlock[1]); } catch (_) {} }
  const objMatch = raw.match(/\{[\s\S]+\}/);
  if (objMatch) { try { return JSON.parse(objMatch[0]); } catch (_) {} }
  return {};
}

/**
 * Categoriza el resultado del enrichment de marca para color-codear la celda
 * "Marca Final" del inspector.
 *   - 'enriched'   : original vacía + final con marca   (verde — value-add)
 *   - 'confirmed'  : original == final                  (azul — confirmación)
 *   - 'changed'    : original != final, ambas no vacías (amarillo — review)
 *   - 'rejected'   : original con marca + final vacía   (rojo — wasted)
 *   - 'neutral'    : ambas vacías o cualquier otro caso
 * Comparación case-insensitive y trim.
 */
type BrandFinalCategory = 'enriched' | 'confirmed' | 'changed' | 'rejected' | 'neutral';
function categorizeBrandFinal(originalRaw: any, finalRaw: any): BrandFinalCategory {
  const norm = (v: any) => (typeof v === 'string' ? v.trim() : (v == null ? '' : String(v).trim()));
  const original = norm(originalRaw);
  const finalV = norm(finalRaw);
  const hasOriginal = original.length > 0;
  const hasFinal = finalV.length > 0;
  if (!hasOriginal && !hasFinal) return 'neutral';
  if (!hasOriginal && hasFinal) return 'enriched';
  if (hasOriginal && !hasFinal) return 'rejected';
  // ambos presentes
  return original.toLowerCase() === finalV.toLowerCase() ? 'confirmed' : 'changed';
}

const BRAND_FINAL_CELL_CLASS: Record<BrandFinalCategory, string> = {
  enriched:  'bg-green-50 border-l-4 border-green-400',
  confirmed: 'bg-blue-50',
  changed:   'bg-yellow-50 border-l-4 border-yellow-400',
  rejected:  'bg-red-50 border-l-4 border-red-400',
  neutral:   '',
};

function flagChips(it: InspectItem): { label: string; color: string }[] {
  const v2 = it.validationV2 || {};
  const out: { label: string; color: string }[] = [];
  if (v2.disagreement) out.push({ label: 'disagree', color: 'bg-amber-100 text-amber-800' });
  if (v2.hallucinationsDetected || v2.not_whitelisted) out.push({ label: 'hallu', color: 'bg-red-100 text-red-800' });
  if (v2.belowThreshold) out.push({ label: 'below', color: 'bg-orange-100 text-orange-800' });
  if (v2.forcedToOtros || v2.forced_to_otros) out.push({ label: 'otros', color: 'bg-purple-100 text-purple-800' });
  if (v2.subcategory_not_in_matrix) out.push({ label: 'subcat?', color: 'bg-yellow-100 text-yellow-800' });
  if (v2.passedWhitelist) out.push({ label: 'wl-ok', color: 'bg-emerald-100 text-emerald-800' });
  if (it.error) out.push({ label: 'error', color: 'bg-red-200 text-red-900' });
  return out;
}

// Componente reusable: cuerpo de la inspección (tabla + filtros + drawer + paginación).
// - embedded=false (default): se usa dentro de InspectItemsModal con su overlay/backdrop.
// - embedded=true: se renderiza inline (subtab Histórico), sin overlay y con header compacto.
const RunItemsBrowser: React.FC<{
  clientId: string;
  run: RunDoc;
  embedded?: boolean;
  onClose?: () => void;
  prefetchedItems?: InspectItem[];
}> = ({ clientId, run, embedded = false, onClose, prefetchedItems }) => {
  const runId = run.runId;
  const [items, setItems] = useState<InspectItem[]>(prefetchedItems || []);
  const [loading, setLoading] = useState(prefetchedItems ? false : true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  // Ref-mirror del cursor: el while loop de ensurePageLoaded encadena fetches
  // y no puede confiar en el state (stale closure entre iteraciones).
  const cursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(prefetchedItems ? false : true);
  const hasMoreRef = useRef<boolean>(prefetchedItems ? false : true);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [filter, setFilter] = useState<InspectFilterKey>('all');
  const [titleFilter, setTitleFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<InspectItem | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  // Solo modo embedded: permite "ampliar" abriendo el browser en modal full-screen.
  const [expanded, setExpanded] = useState(false);
  // Modal de imagen ampliada (reusa ImageModal global del archivo).
  const [imgOpen, setImgOpen] = useState(false);
  // Cache in-memory de enrichments por idCatalogo, válido mientras viva el componente.
  const enrichCacheRef = useRef<Map<string, { imagen?: string | null; url?: string | null; loading?: boolean }>>(new Map());
  const [, setEnrichTick] = useState(0); // tick para forzar rerender cuando llega enrichment

  const PAGE_SIZE = 50;

  // Formato fecha local DD/MM/YYYY HH:mm (sin UTC).
  const triggeredAtLabel = (() => {
    if (!run.triggeredAt) return '—';
    const d = new Date(run.triggeredAt);
    if (Number.isNaN(d.getTime())) return run.triggeredAt;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();

  useEffect(() => {
    // Si recibimos items pre-cargados, evitamos un fetch redundante (caso del
    // modal ampliado abierto desde el embedded: comparten dataset).
    // Nota: el dataset pre-cargado se considera completo solo cuando filter==='all'.
    // Si el usuario cambia el filtro, refetcheamos desde backend para cubrir el TOTAL.
    if (prefetchedItems && filter === 'all') {
      setItems(prefetchedItems);
      setLoading(false);
      setHasMore(false);
      hasMoreRef.current = false;
      cursorRef.current = null;
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrMsg(null);
    // Reset estado de paginación al cambiar filter — el backend va a re-paginar
    // sobre el universo filtrado, por lo que cursor previo no aplica.
    setItems([]);
    cursorRef.current = null;
    setCursor(null);
    setPage(0);
    // Carga incremental: 100 ítems iniciales con cursor. La paginación auto-load
    // (ensurePageLoaded) trae lotes adicionales bajo demanda al navegar.
    getRunItems(clientId, runId, { limit: 100, filter })
      .then(r => {
        if (cancelled) return;
        setItems(r.items || []);
        setCursor(r.nextCursor || null);
        cursorRef.current = r.nextCursor || null;
        setHasMore(!!r.hasMore);
        hasMoreRef.current = !!r.hasMore;
      })
      .catch(e => { if (!cancelled) setErrMsg(e?.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, runId, prefetchedItems, filter]);

  // Enriquecimiento on-demand desde toma_*: runs viejos no persisten imagen/url
  // en el itemDoc de Firestore. Cuando se selecciona un item al que le falta
  // alguno de esos campos, lo pedimos al backend y cacheamos por idCatalogo.
  useEffect(() => {
    if (!selected) return;
    const idc = selected.idCatalogo != null ? String(selected.idCatalogo) : null;
    if (!idc) return;
    if (selected.imagen && selected.url) return; // nada que completar
    const cached = enrichCacheRef.current.get(idc);
    if (cached && !cached.loading) return; // ya resolvimos antes
    if (cached && cached.loading) return;  // request en vuelo
    enrichCacheRef.current.set(idc, { loading: true });
    setEnrichTick(t => t + 1);
    let cancelled = false;
    getRunItemEnrichment(clientId, runId, idc)
      .then(r => {
        if (cancelled) return;
        const row = r?.row || null;
        enrichCacheRef.current.set(idc, {
          imagen: row?.imagen ?? null,
          url: row?.url ?? null,
          loading: false,
        });
        setEnrichTick(t => t + 1);
      })
      .catch(() => {
        if (cancelled) return;
        enrichCacheRef.current.set(idc, { loading: false });
        setEnrichTick(t => t + 1);
      });
    return () => { cancelled = true; };
  }, [selected, clientId, runId]);

  // Mapa idCatalogo → "# procesado". Si el itemDoc tiene processIndex (runs
  // nuevos post-2026-05-12), lo usamos directo. Si no, lo derivamos ordenando
  // por startedAt (ISO comparable lexicográficamente) y asignamos 1..N entre
  // los items cargados — best-effort para runs viejos.
  const processIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    // Caso 1: todos tienen processIndex → uso directo.
    const allHaveIdx = items.length > 0 && items.every(it => typeof it.processIndex === 'number');
    if (allHaveIdx) {
      for (const it of items) {
        if (it.idCatalogo != null) map.set(String(it.idCatalogo), it.processIndex);
      }
      return map;
    }
    // Caso 2: derivar por startedAt (fallback runs viejos).
    const sortable = items
      .map((it, i) => ({ id: it.idCatalogo != null ? String(it.idCatalogo) : `__idx${i}`,
                         t: typeof it.startedAt === 'string' ? it.startedAt : '',
                         orig: i }))
      .sort((a, b) => {
        if (a.t && b.t) return a.t < b.t ? -1 : a.t > b.t ? 1 : 0;
        if (a.t) return -1; if (b.t) return 1;
        return a.orig - b.orig;
      });
    sortable.forEach((s, i) => map.set(s.id, i + 1));
    return map;
  }, [items]);

  // El filtrado por flag (filter) se hace SERVER-side: cuando cambia, el
  // useEffect de arriba refetchea con ?filter=X cubriendo TODOS los items
  // del run. Acá solo aplicamos el sub-filtro de title client-side sobre lo
  // ya cargado (es búsqueda free-text).
  const filtered = useMemo(() => {
    const q = titleFilter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it => (it.title || '').toLowerCase().includes(q));
  }, [items, titleFilter]);
  useEffect(() => { setPage(0); }, [titleFilter]);

  // Source of truth de paginación:
  // - filter==='all': TOTAL real del run (productsTotal del RunDoc).
  // - filter !=='all': desconocemos el total filtrado hasta agotar el cursor;
  //   usamos lo cargado + indicador hasMore para permitir seguir paginando.
  const hasFilters = !!titleFilter.trim() || filter !== 'all';
  const total = filter === 'all'
    ? ((typeof run.productsTotal === 'number' && run.productsTotal > 0) ? run.productsTotal : items.length)
    : (items.length + (hasMore ? PAGE_SIZE : 0)); // hint: si hasMore, hay al menos un page más
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Auto-load: garantiza que los items necesarios para `targetPage` estén
  // cargados, encadenando fetches de 100 si hace falta. Usa refs para evitar
  // stale closures entre iteraciones del while + un contador local de longitud
  // (porque setItems no actualiza `items` dentro de la misma ejecución).
  const ensurePageLoaded = async (targetPage: number) => {
    // Solo válido el dataset pre-cargado cuando estamos en filter==='all' (mismo
    // caso del useEffect inicial). Con filter activo, refetcheamos siempre.
    if (prefetchedItems && filter === 'all') return;
    const pageEnd = (targetPage + 1) * PAGE_SIZE;
    if (loadingMoreRef.current) return;
    let loadedLen = items.length;
    while (loadedLen < pageEnd && hasMoreRef.current) {
      loadingMoreRef.current = true;
      setLoadingMore(true);
      try {
        const r = await getRunItems(clientId, runId, { limit: 100, cursor: cursorRef.current, filter });
        const newItems = r.items || [];
        loadedLen += newItems.length;
        cursorRef.current = r.nextCursor || null;
        hasMoreRef.current = !!r.hasMore;
        setItems(prev => [...prev, ...newItems]);
        setCursor(cursorRef.current);
        setHasMore(hasMoreRef.current);
        // Safety: si el backend devolvió 0 items pero dijo hasMore, salimos.
        if (newItems.length === 0) { hasMoreRef.current = false; setHasMore(false); break; }
      } catch (e: any) {
        setErrMsg(e?.response?.data?.error || e.message);
        hasMoreRef.current = false;
        break;
      } finally {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  };

  const goToPage = (p: number) => {
    const clamped = Math.max(0, Math.min(totalPages - 1, p));
    setPage(clamped);
    void ensurePageLoaded(clamped);
  };

  // En modo embedded usamos altura acotada (no 100vh) y borde redondeado,
  // sin botón cerrar. En modo modal mantenemos el comportamiento full-screen.
  const containerClass = embedded
    ? 'bg-white border border-gray-200 rounded-lg w-full flex flex-col overflow-hidden h-[680px]'
    : 'bg-white shadow-xl border border-gray-200 w-full h-full flex flex-col';

  return (
    <>
    <div className={containerClass}>
        {/* Header — title grande + run id + fecha/hora + meta del run */}
        <div className={`flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-slate-50 to-slate-100 ${embedded ? 'px-3 py-2' : 'px-5 py-3'}`}>
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-blue-600 rounded-md">
              <Search className="w-4 h-4 text-white" />
            </div>
            <div>
              <h4 className={`font-bold text-gray-900 leading-tight ${embedded ? 'text-sm' : 'text-lg'}`}>
                Inspección de Run
              </h4>
              <div className="flex items-center gap-3 text-xs text-gray-600 tabular-nums mt-0.5">
                <button
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(runId);
                    } catch {/* ignore */}
                  }}
                  title="Click para copiar run ID completo"
                  className="font-mono bg-white border border-gray-200 px-1.5 py-0.5 rounded hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors cursor-pointer"
                >
                  {runId.slice(0, 8)}
                </button>
                <span>{triggeredAtLabel}</span>
                <span className="text-gray-400">·</span>
                <span>{loading ? 'cargando…' : `${total.toLocaleString('es-AR')} ítems${hasFilters ? ` (${filtered.length.toLocaleString('es-AR')} visibles tras filtros)` : ''}`}</span>
                {run.runType && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span className="uppercase text-[10px] font-semibold text-gray-500">{run.runType}</span>
                  </>
                )}
                {/* Total tokens in/out — movido desde la tabla del histórico al header del inspector */}
                {(run.tokensIn != null || run.tokensOut != null) && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span title="Tokens del LLM consumidos en este run">
                      <span className="text-gray-500">Tokens:</span>{' '}
                      <span className="font-medium tabular-nums">{(run.tokensIn || 0).toLocaleString('es-AR')}</span>
                      <span className="text-gray-400"> in</span>
                      <span className="text-gray-400"> / </span>
                      <span className="font-medium tabular-nums">{(run.tokensOut || 0).toLocaleString('es-AR')}</span>
                      <span className="text-gray-400"> out</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {embedded && (
              <button
                onClick={() => setExpanded(true)}
                className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-700 hover:bg-white border border-gray-300 rounded transition-colors"
                title="Abrir en pantalla completa"
              >
                <Maximize2 className="w-3.5 h-3.5" />
                Ampliar
              </button>
            )}
            {!embedded && onClose && (
              <button onClick={onClose} className="p-1.5 hover:bg-white rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filtros + búsqueda por title (substring, case-insensitive, live) */}
        <div className="px-4 py-2 border-b border-gray-200 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {INSPECT_FILTERS.map(f => {
              const active = filter === f.key;
              // Con filtrado server-side, solo podemos mostrar el count del
              // filtro actualmente activo (items en memoria + hint si hay más).
              // Para los demás chips mostramos el label sin count.
              const showCount = active;
              const count = showCount
                ? (items.length + (hasMore ? '+' : ''))
                : null;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
                    active
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {f.label}{count !== null && <> <span className="tabular-nums opacity-70">({count})</span></>}
                </button>
              );
            })}
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <input
              type="text"
              value={titleFilter}
              onChange={e => setTitleFilter(e.target.value)}
              placeholder="Buscar por título…"
              className="text-[11px] border border-gray-300 rounded px-2 py-0.5 w-56 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {titleFilter && (
              <button
                onClick={() => setTitleFilter('')}
                className="p-0.5 text-gray-400 hover:text-gray-600"
                title="Limpiar"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Body: tabla + drawer lateral.
            En modo embedded el drawer queda en 1/3. En modal/expanded usamos
            1/2 para que el grid 50/50 (imagen | datos) tenga aire suficiente. */}
        <div className="flex-1 flex overflow-hidden">
          <div className={`${selected ? (embedded ? 'w-2/3' : 'w-1/2') : 'w-full'} overflow-auto transition-all`}>
            {loading && (
              <div className="p-6 flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando items…
              </div>
            )}
            {errMsg && (
              <div className="m-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {errMsg}
              </div>
            )}
            {/* Caso especial: runs en modo upload-only NO escriben per-item docs a
                Firestore (bulk INSERT directo a MariaDB para velocidad). El inspector
                detecta esto y muestra un empty state explicativo en lugar de tabla vacía. */}
            {!loading && !errMsg && items.length === 0 && run.executionMode === 'upload-only' && (
              <div className="p-6 max-w-2xl mx-auto text-sm">
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-1.5 bg-amber-100 rounded">
                      <AlertTriangle className="w-4 h-4 text-amber-700" />
                    </div>
                    <div>
                      <div className="font-semibold text-amber-900 mb-1">Run modo "Upload-only" — sin detalle per-item</div>
                      <p className="text-amber-800 mb-2">
                        Este run hizo <b>bulk INSERT directo a MariaDB</b> con datos del LLM ya hechos en runs previos. Por performance, NO se escribieron documentos por item en Firestore.
                      </p>
                      <p className="text-amber-800 mb-3">
                        Los <b className="tabular-nums">{(run.productsProcessed || 0).toLocaleString('es-AR')} productos procesados</b> están en la tabla <code className="bg-amber-100 px-1 rounded">catalog</code> con su <code>catalog_brand</code>, <code>catalog_category</code>, <code>subcategory</code>, <code>segmento</code> y demás campos.
                      </p>
                      <div className="text-[11px] text-amber-700">
                        Para ver detalle del LLM (rawResponse, tokens, reasoning, validación V2) abrí el <b>run origen</b> que ejecutó el LLM — ese sí tiene items per-doc.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!loading && !errMsg && (items.length > 0 || run.executionMode !== 'upload-only') && (
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-2 py-1 text-right w-12" title="Orden de procesamiento">#</th>
                    <th className="px-2 py-1 text-left">Title</th>
                    <th className="px-2 py-1 text-left">Marca origen</th>
                    <th className="px-2 py-1 text-left">Marca final</th>
                    <th className="px-2 py-1 text-left">Cat origen</th>
                    <th className="px-2 py-1 text-left">Cat final</th>
                    <th className="px-2 py-1 text-left">Subcat</th>
                    <th className="px-2 py-1 text-left">Segmento</th>
                    <th className="px-2 py-1 text-right">Conf</th>
                    <th className="px-2 py-1 text-left">Flags</th>
                    <th className="px-2 py-1 text-right">Costo</th>
                    <th className="px-2 py-1 text-right">Lat</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((it, idx) => {
                    const isSel = selected && selected.idCatalogo === it.idCatalogo;
                    const finalBrandVal = it.finalBrand || it.parsedBrand || '';
                    const brandCat = categorizeBrandFinal(it.marcaOriginal, finalBrandVal);
                    const brandCellClass = BRAND_FINAL_CELL_CLASS[brandCat];
                    const pIdKey = it.idCatalogo != null ? String(it.idCatalogo) : `__idx${idx}`;
                    const pIdx = processIndexMap.get(pIdKey);
                    return (
                      <tr
                        key={`${it.idCatalogo || idx}`}
                        onClick={() => { setSelected(it); setShowPrompt(false); setShowRaw(false); }}
                        className={`border-b border-gray-100 cursor-pointer ${
                          isSel ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-2 py-1 text-right tabular-nums text-gray-500">
                          {typeof pIdx === 'number' ? pIdx : '—'}
                        </td>
                        <td className="px-2 py-1 max-w-[320px] truncate" title={it.title || ''}>
                          {it.title || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-2 py-1">{it.marcaOriginal || '—'}</td>
                        <td
                          className={`px-2 py-1 font-medium ${brandCellClass}`}
                          title={
                            brandCat === 'enriched' ? 'Enriquecimiento: marca agregada por el LLM'
                              : brandCat === 'confirmed' ? 'Confirmación: original == final'
                              : brandCat === 'changed' ? 'Cambio: revisar — original difiere del final'
                              : brandCat === 'rejected' ? 'Rechazo: marca original descartada'
                              : ''
                          }
                        >
                          {finalBrandVal || '—'}
                        </td>
                        <td className="px-2 py-1">{it.catOriginal || '—'}</td>
                        <td className="px-2 py-1 font-medium">{it.finalCategory || it.parsedCategory || '—'}</td>
                        <td className="px-2 py-1">
                          {it.parsed?.subcategory || it.validationV2?.subcategory_suggested || '—'}
                        </td>
                        <td className="px-2 py-1">{it.finalSegmento || it.parsedSegmento || '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {typeof it.confidence === 'number' ? it.confidence.toFixed(2) : '—'}
                        </td>
                        <td className="px-2 py-1">
                          <div className="flex gap-0.5 flex-wrap">
                            {flagChips(it).map((c, i) => (
                              <span key={i} className={`px-1 rounded text-[10px] ${c.color}`}>{c.label}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {typeof it.costUsd === 'number' ? `$${it.costUsd.toFixed(5)}` : '—'}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-600">
                          {typeof it.durationMs === 'number' ? `${it.durationMs}ms` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan={12} className="px-2 py-6 text-center text-gray-500">
                        No hay items para este filtro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Drawer derecho con detalle */}
          {selected && (() => {
            // Mergeamos imagen/url desde el cache de enrichment cuando el itemDoc
            // de Firestore no las trae (runs viejos). Nunca sobreescribimos lo
            // existente.
            const idc = selected.idCatalogo != null ? String(selected.idCatalogo) : null;
            const enriched = idc ? enrichCacheRef.current.get(idc) : undefined;
            const enrichLoading = !!enriched?.loading;
            const effImagen = selected.imagen || enriched?.imagen || null;
            const effUrl = selected.url || enriched?.url || null;

            // Fallback desde rawLLMResponse para runs viejos que no persistieron
            // todos los campos. Política: persistido > raw parseado > null.
            const rawSrc = selected.rawLLMResponse || selected.rawResponse || '';
            const rawParsed = parseRawLLMResponse(rawSrc);
            const effReasoning = selected.reasoning
              || selected.parsed?.reasoning
              || selected.final?.reasoning
              || rawParsed.reasoning
              || null;
            const effParsedBrand = selected.parsedBrand || rawParsed.catalog_brand || null;
            const effParsedCategory = selected.parsedCategory || rawParsed.catalog_category || null;
            const effParsedSubcategory = selected.parsedSubcategory
              || selected.parsed?.subcategory
              || rawParsed.subcategory
              || null;
            const effParsedSegmento = selected.parsedSegmento || rawParsed.segmento || null;
            const effParsedContenido = selected.parsedContenido || rawParsed.contenido || null;
            const effParsedUdm = selected.parsedUdm || rawParsed.udm || null;
            const effFinalSubcategory = selected.finalSubcategory || effParsedSubcategory;
            const effConfidence = typeof selected.confidence === 'number'
              ? selected.confidence
              : (typeof rawParsed.confidence_score === 'number' ? rawParsed.confidence_score : null);

            const confColor = typeof effConfidence === 'number'
              ? effConfidence >= 0.9 ? 'bg-emerald-100 text-emerald-700'
                : effConfidence >= 0.7 ? 'bg-amber-100 text-amber-700'
                : 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-500';
            return (
            <div className={`${embedded ? 'w-1/3' : 'w-1/2'} border-l border-gray-200 overflow-auto bg-gray-50`}>
              <div className="sticky top-0 bg-white border-b border-gray-200 px-3 py-2 flex items-center justify-between z-10">
                <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider">
                  Detalle del ítem
                </div>
                <button onClick={() => setSelected(null)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="p-3 space-y-3 text-[11px]">
                {/* Title completo + id_catalogo en card propia */}
                <section className="bg-white border border-gray-200 rounded p-2">
                  <div className="text-sm font-semibold text-gray-900 break-words leading-snug">
                    {selected.title || <span className="text-gray-400 font-normal">(sin título)</span>}
                  </div>
                  <div className="text-[10px] text-gray-500 tabular-nums mt-1">
                    id_catalogo: {selected.idCatalogo}
                  </div>
                </section>

                {/* Confidence inline pill + flags en una fila */}
                <div className="bg-white border border-gray-200 rounded p-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold tabular-nums ${confColor}`}
                          title="Confidence">
                      {typeof effConfidence === 'number' ? effConfidence.toFixed(2) : '—'}
                    </span>
                    {flagChips(selected).length > 0
                      ? flagChips(selected).map((c, i) => (
                          <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.color}`}>{c.label}</span>
                        ))
                      : <span className="text-[10px] text-gray-400">sin flags</span>}
                  </div>
                </div>

                {/* Imagen + DATOS DE ORIGEN.
                    En modo embedded: stacked vertical (ancho del drawer es chico).
                    En modo modal/expanded: 50/50 lado a lado, mismo alto. */}
                <div className={embedded ? 'space-y-3' : 'grid grid-cols-2 gap-3'}>
                  {/* Imagen + Ver + link */}
                  {(effImagen || effUrl || enrichLoading) ? (
                    <div className="bg-white border border-gray-200 rounded overflow-hidden flex flex-col">
                      {effImagen && (
                        <div className="relative flex-1 flex items-center justify-center bg-white min-h-[160px]">
                          <img src={effImagen} alt="" className={`w-full object-contain bg-white ${embedded ? 'max-h-44' : 'max-h-[260px]'}`} />
                          <button
                            onClick={() => setImgOpen(true)}
                            className="absolute top-1 right-1 px-2 py-0.5 text-[10px] bg-white/90 hover:bg-white border border-gray-300 rounded shadow-sm"
                            title="Ver imagen ampliada"
                          >
                            Ver
                          </button>
                        </div>
                      )}
                      {effUrl && (
                        <a href={effUrl} target="_blank" rel="noopener noreferrer"
                           className="block px-2 py-1.5 text-[11px] text-blue-600 hover:bg-blue-50 hover:underline truncate border-t border-gray-100"
                           title={effUrl}>
                          🔗 Abrir listing en nueva pestaña
                        </a>
                      )}
                      {enrichLoading && !effImagen && !effUrl && (
                        <div className="px-2 py-2 flex items-center gap-1.5 text-[10px] text-gray-500">
                          <Loader2 className="w-3 h-3 animate-spin" /> Buscando imagen/URL en TOMA…
                        </div>
                      )}
                    </div>
                  ) : (
                    !embedded && (
                      <div className="bg-white border border-gray-200 rounded p-3 flex items-center justify-center text-[10px] text-gray-400 min-h-[160px]">
                        Sin imagen disponible
                      </div>
                    )
                  )}

                  {/* DATOS DE ORIGEN (TOMA) */}
                  <section className="bg-white border border-gray-200 rounded p-2 flex flex-col">
                    <div className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider mb-1.5">Datos de origen (TOMA)</div>
                    <div className="space-y-0.5 flex-1">
                      <div><span className="text-gray-500">id_catalogo:</span> <span className="tabular-nums">{selected.idCatalogo}</span></div>
                      <div><span className="text-gray-500">Marca:</span> {selected.marcaOriginal || <span className="text-gray-400">—</span>}</div>
                      <div><span className="text-gray-500">Cat:</span> {selected.catOriginal || <span className="text-gray-400">—</span>}</div>
                      <div><span className="text-gray-500">Modelo:</span> {selected.model || '—'} <span className="text-gray-400">({selected.provider || '—'})</span></div>
                    </div>
                    {selected.tokensIn != null && (
                      <div className="tabular-nums text-gray-600 mt-1 pt-1 border-t border-gray-100">
                        <span className="text-gray-500">Tokens:</span> {selected.tokensIn}/{selected.tokensOut}
                        {' · '}<span className="text-gray-500">Costo:</span> ${(selected.costUsd || 0).toFixed(5)}
                        {' · '}<span className="text-gray-500">Lat:</span> {selected.durationMs || 0}ms
                      </div>
                    )}
                  </section>
                </div>

                {/* ENRICHMENT — tabla con columna Final destacada */}
                <section className="bg-white border border-gray-200 rounded overflow-hidden">
                  <div className="text-[10px] uppercase font-semibold text-gray-700 tracking-wider px-2 py-1.5 bg-slate-100 border-b border-gray-200">
                    Enrichment
                  </div>
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
                      <tr>
                        <th className="text-left font-semibold uppercase text-[9px] tracking-wide px-2 py-1 w-[80px]">Campo</th>
                        <th className="text-left font-semibold uppercase text-[9px] tracking-wide px-2 py-1">Toma</th>
                        <th className="text-left font-semibold uppercase text-[9px] tracking-wide px-2 py-1">LLM</th>
                        <th className="text-left font-semibold uppercase text-[9px] tracking-wide px-2 py-1 bg-emerald-50 text-emerald-700 border-l border-emerald-200">Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {([
                        { label: 'Marca', toma: selected.marcaOriginal, llm: effParsedBrand, final: selected.finalBrand || effParsedBrand },
                        { label: 'Categoría', toma: selected.catOriginal, llm: effParsedCategory, final: selected.finalCategory || effParsedCategory },
                        { label: 'Subcat', toma: null, llm: effParsedSubcategory, final: effFinalSubcategory },
                        { label: 'Segmento', toma: null, llm: effParsedSegmento, final: selected.finalSegmento || effParsedSegmento },
                        { label: 'Contenido', toma: null, llm: effParsedContenido, final: selected.finalContenido || effParsedContenido },
                        { label: 'UDM', toma: null, llm: effParsedUdm, final: selected.finalUdm || effParsedUdm },
                      ] as { label: string; toma: any; llm: any; final: any }[]).map((row, i) => (
                        <tr key={row.label} className={i > 0 ? 'border-t border-gray-100' : ''}>
                          <td className="text-gray-500 font-medium px-2 py-1">{row.label}</td>
                          <td className="px-2 py-1 text-gray-600">{row.toma || <span className="text-gray-300">—</span>}</td>
                          <td className="px-2 py-1 text-gray-600">{row.llm || <span className="text-gray-300">—</span>}</td>
                          <td className="px-2 py-1 bg-emerald-50/40 border-l border-emerald-200 font-semibold text-gray-900">
                            {row.final || <span className="text-gray-300 font-normal">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>

                {/* Reasoning del LLM — siempre abierto, no colapsable.
                    Fallback automático desde rawLLMResponse para runs viejos. */}
                <section className="bg-white border border-gray-200 rounded overflow-hidden">
                  <div className="text-[10px] uppercase font-semibold text-gray-700 tracking-wider px-2 py-1.5 bg-slate-100 border-b border-gray-200">
                    Reasoning
                  </div>
                  <div className="p-2 text-[11px] text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                    {effReasoning || <span className="text-gray-400 italic">(sin reasoning ni en itemDoc ni en raw response)</span>}
                  </div>
                </section>

                {/* Notes del validador */}
                {Array.isArray(selected.validationResult?.notes) && selected.validationResult.notes.length > 0 && (
                  <section className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="text-[10px] uppercase font-semibold text-amber-800 tracking-wider mb-1">Notes del validador</div>
                    <ul className="list-disc list-inside text-[10px] text-amber-900 space-y-0.5">
                      {selected.validationResult.notes.map((n: string, i: number) => <li key={i}>{n}</li>)}
                    </ul>
                  </section>
                )}

                {/* Validation V2 raw (colapsable) */}
                <details className="bg-white border border-gray-200 rounded">
                  <summary className="cursor-pointer px-2 py-1.5 text-[10px] uppercase font-semibold text-gray-500 tracking-wider hover:bg-gray-50">
                    Validation V2 (raw)
                  </summary>
                  <pre className="text-[10px] bg-gray-50 p-2 overflow-auto max-h-48 border-t border-gray-100">
{JSON.stringify(selected.validationV2 || {}, null, 2)}
                  </pre>
                </details>

                {selected.error && (
                  <section className="bg-red-50 border border-red-200 rounded p-2">
                    <div className="text-[10px] uppercase font-semibold text-red-700 mb-1">Error</div>
                    <pre className="text-[10px] whitespace-pre-wrap break-all">{selected.error}</pre>
                  </section>
                )}

                <section className="bg-white border border-gray-200 rounded">
                  <button
                    onClick={() => setShowPrompt(p => !p)}
                    className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-50"
                  >
                    <span className="text-[10px] uppercase font-semibold text-gray-500">Resolved prompt</span>
                    {showPrompt ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                  {showPrompt && (
                    <pre className="text-[10px] bg-gray-50 p-2 max-h-72 overflow-auto whitespace-pre-wrap break-words border-t border-gray-100">
{selected.resolvedPrompt || '(vacío)'}
                    </pre>
                  )}
                </section>

                <section className="bg-white border border-gray-200 rounded">
                  <button
                    onClick={() => setShowRaw(r => !r)}
                    className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-gray-50"
                  >
                    <span className="text-[10px] uppercase font-semibold text-gray-500">Raw LLM response</span>
                    {showRaw ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                  {showRaw && (
                    <pre className="text-[10px] bg-gray-50 p-2 max-h-72 overflow-auto whitespace-pre-wrap break-words border-t border-gray-100">
{selected.rawLLMResponse || selected.rawResponse || '(vacío)'}
                    </pre>
                  )}
                </section>
              </div>

              {/* Modal global de imagen ampliada (reusa ImageModal). */}
              {imgOpen && effImagen && (
                <ImageModal
                  isOpen={imgOpen}
                  onClose={() => setImgOpen(false)}
                  imageUrl={effImagen}
                  productTitle={selected.title || `id ${selected.idCatalogo}`}
                  productUrl={effUrl || undefined}
                />
              )}
            </div>
            );
          })()}
        </div>

        {/* Footer: paginación. Total = run.productsTotal (source of truth).
            Auto-load silencioso al navegar a páginas no cargadas todavía. */}
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-[11px]">
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page === 0}
              className="px-2 py-0.5 border border-gray-300 rounded hover:bg-white disabled:opacity-40"
            >
              ← Prev
            </button>
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages - 1}
              className="px-2 py-0.5 border border-gray-300 rounded hover:bg-white disabled:opacity-40"
            >
              Next →
            </button>
            <div className="text-gray-600 tabular-nums ml-2">
              Página {page + 1} de {totalPages} · {total.toLocaleString('es-AR')} ítems
              {hasFilters && (
                <span className="text-gray-400"> ({filtered.length.toLocaleString('es-AR')} visibles tras filtros)</span>
              )}
            </div>
            {loadingMore && (
              <span className="flex items-center gap-1 text-gray-500 ml-2">
                <Loader2 className="w-3 h-3 animate-spin" /> cargando…
              </span>
            )}
          </div>
        </div>
    </div>
    {/* Modal full-screen abierto desde el botón "Ampliar" en modo embedded.
        Le pasamos los items pre-cargados para evitar un segundo fetch a Firestore. */}
    {embedded && expanded && (
      <InspectItemsModal
        clientId={clientId}
        run={run}
        onClose={() => setExpanded(false)}
        prefetchedItems={items}
      />
    )}
    </>
  );
};

// Wrapper modal: overlay + backdrop full-screen alrededor de RunItemsBrowser.
const InspectItemsModal: React.FC<{
  clientId: string;
  run: RunDoc;
  onClose: () => void;
  prefetchedItems?: InspectItem[];
}> = ({ clientId, run, onClose, prefetchedItems }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40">
      <RunItemsBrowser
        clientId={clientId}
        run={run}
        embedded={false}
        onClose={onClose}
        prefetchedItems={prefetchedItems}
      />
    </div>
  );
};

// =============================================================================
// BATCH
// =============================================================================

type CatalogStats = {
  total: number;
  cataloged: number;
  pending: number;
  iaSplit?: {
    pendingNoIa: number;
    withIaPendingUpload: number;
    withIaErrored?: number;
    cataloged: number;
    total: number;
  };
};
type TomaMeta = {
  tomaDate: string | null;
  sitesCount: number;
  sites: string[];
  lastCatalogUpdate: string | null;
  manuallyEditedCount: number;
};

const BatchSubtab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [tables, setTables] = useState<string[]>([]);
  const [tableName, setTableName] = useState<string>('');
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [tomaMeta, setTomaMeta] = useState<TomaMeta | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [run, setRun] = useState<RunDoc | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Mensaje de actividad mientras el run arranca (antes de tener datos del backend).
  const [activityStep, setActivityStep] = useState<string | null>(null);
  // Config del prompt activo — para mostrar resumen al usuario.
  const [activePrompt, setActivePrompt] = useState<CatalogPrompt | null>(null);
  // Modo unificado: muestra (con cantidad), todos pendientes, o re-catalogar todo.
  const [batchMode, setBatchMode] = useState<'sample' | 'pending' | 'all'>('sample');
  const [sampleSize, setSampleSize] = useState<number>(100);
  // Default false — el user explicito tilda si quiere estratificar.
  const [useStratified, setUseStratified] = useState<boolean>(false);
  // Track si el user manualmente cambió `sampleSize` desde el preset inicial,
  // para decidir si auto-actualizamos el "preview" cuando cambian pendientes/total.
  const userTouchedSampleRef = useRef<boolean>(false);

  // Inspección de items del run actual / último run cargado.
  const [inspectOpen, setInspectOpen] = useState(false);

  // Tick para refrescar elapsed/ETA aunque no llegue nuevo poll del run.
  // Sin esto, el contador "elapsed" solo subiría cada 2s (cuando llega el poll).
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (run?.status !== 'processing') return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [run?.status]);

  // Checkpoints rolling para velocidad de los últimos 1000 productos.
  // Cada vez que processed cruza un múltiplo de 1000, guardamos {count, ts}.
  // Para calcular velocidad rolling: comparar último checkpoint vs ahora.
  // Persiste en useRef para sobrevivir re-renders sin gatillarlos.
  const checkpointsRef = useRef<{ count: number; ts: number }[]>([]);
  const lastBucketRef = useRef<number>(-1);
  const startedAtMsRef = useRef<number | null>(null);

  useEffect(() => {
    api.get('/api/catalog/tables', { headers: { 'x-client-id': clientId } })
      .then(r => {
        const raw = (r.data as string[]) || [];
        const t = filterAndSortTomaTables(raw);
        setTables(t);
        if (t.length && !tableName) setTableName(t[0]);
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, [clientId]);

  // Cargar stats (total/cataloged/pending + iaSplit) — extraído como helper reusable.
  const refreshStats = async () => {
    if (!tableName) return;
    setStatsLoading(true);
    try {
      const [statsR, metaR] = await Promise.all([
        api.get(`/api/catalog/stats/${tableName}?iaSplit=true`, { headers: { 'x-client-id': clientId } }),
        api.get(`/api/catalog/toma-meta/${tableName}`, { headers: { 'x-client-id': clientId } }),
      ]);
      setStats(statsR.data);
      setTomaMeta(metaR.data);
    } catch (_) {
      setStats(null);
      setTomaMeta(null);
    } finally {
      setStatsLoading(false);
    }
  };
  useEffect(() => {
    // Reset del "touched" flag al cambiar de tabla — el preview vuelve a ser default.
    userTouchedSampleRef.current = false;
    refreshStats();
    /* eslint-disable-next-line */
  }, [tableName, clientId]);

  // Cargar el prompt activo del cliente (config en uso) — solo para mostrar.
  useEffect(() => {
    let cancelled = false;
    listPrompts(clientId)
      .then(r => {
        if (cancelled) return;
        const active = (r.prompts || []).find(p => p.isActive) || null;
        setActivePrompt(active);
      })
      .catch(() => { if (!cancelled) setActivePrompt(null); });
    return () => { cancelled = true; };
  }, [clientId]);

  // Auto-refresh stats cuando un run termina (transición processing → completed/error/cancelled).
  const prevStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const cur = run?.status || null;
    const prev = prevStatusRef.current;
    if (prev === 'processing' && cur && cur !== 'processing') {
      refreshStats();
    }
    prevStatusRef.current = cur;
    // eslint-disable-next-line
  }, [run?.status]);

  // poll active run
  useEffect(() => {
    if (!activeRunId) return;
    // Reset de checkpoints/startedAt al arrancar un run nuevo.
    checkpointsRef.current = [];
    lastBucketRef.current = -1;
    startedAtMsRef.current = null;

    const id = setInterval(async () => {
      try {
        const r = await getRun(clientId, activeRunId);
        setRun(r);

        // Registrar startedAt local en cuanto tengamos triggeredAt del backend.
        // (Usamos triggeredAt como inicio del run.)
        if (startedAtMsRef.current == null && r.triggeredAt) {
          const t = Date.parse(r.triggeredAt);
          if (!Number.isNaN(t)) startedAtMsRef.current = t;
        }

        // Checkpoint cada vez que el processed cruza un múltiplo de 1000.
        const processed = r.productsProcessed + r.productsFailed;
        const currentBucket = Math.floor(processed / 1000);
        if (currentBucket > lastBucketRef.current) {
          checkpointsRef.current.push({ count: processed, ts: Date.now() });
          lastBucketRef.current = currentBucket;
          // Mantener solo los últimos 5 checkpoints (anti-leak).
          if (checkpointsRef.current.length > 5) checkpointsRef.current.shift();
        }

        if (r.status !== 'processing') setActiveRunId(null);
      } catch {}
    }, 2000);
    return () => clearInterval(id);
  }, [activeRunId, clientId]);

  /**
   * Handler unificado para los 3 modos: muestra parcial, todos pendientes, re-catalogar todo.
   * Limpia el run anterior INMEDIATAMENTE y muestra activity steps mientras arranca,
   * para evitar la sensación de "spinner sin feedback" antes de que llegue el primer poll.
   */
  const startUnified = async () => {
    if (!tableName) return;

    // Confirmación destructiva solo para 'all'
    if (batchMode === 'all') {
      const ok = window.confirm(
        `RE-CATALOGAR TODO (${stats?.total ?? '?'} productos)\n\n` +
        `Esto correrá la IA sobre TODOS los productos, sobreescribiendo los catalogados existentes (excepto campos editados manualmente). Costo estimado depende del modelo activo.\n\n¿Confirmar?`
      );
      if (!ok) return;
    }

    // Limpiar UI inmediatamente para mostrar feedback visible.
    setLoading(true);
    setErrMsg(null);
    setRun(null);
    setActiveRunId(null);
    checkpointsRef.current = [];
    lastBucketRef.current = -1;
    startedAtMsRef.current = null;
    setActivityStep('Iniciando…');

    try {
      if (batchMode === 'sample') {
        if (sampleSize <= 0) { setErrMsg('Cantidad debe ser > 0'); return; }
        setActivityStep(`Buscando ${sampleSize * 5} productos pendientes (random)…`);
        const poolLimit = Math.max(sampleSize * 5, sampleSize);
        const r = await api.get('/api/catalog/search-products', {
          headers: { 'x-client-id': clientId },
          params: {
            table: tableName,
            limit: poolLimit,
            offset: 0,
            onlyUncataloged: 'true',
            random: 'true',
          },
        });
        const pool: ProductRow[] = r.data.products || [];
        if (pool.length === 0) {
          setErrMsg('No hay productos pendientes para catalogar.');
          return;
        }
        setActivityStep(`Sampleando ${sampleSize} de ${pool.length}${useStratified ? ' (estratificado por Cat)' : ''}…`);
        const sampled = useStratified
          ? stratifiedSampleByCat(pool, sampleSize, 0.15)
          : shuffleArray(pool).slice(0, sampleSize);
        const productIds = sampled.map(p => p.id_catalogo);
        if (productIds.length === 0) { setErrMsg('Sample vacío.'); return; }
        setActivityStep(`Enviando ${productIds.length} productos al backend…`);
        const { runId } = await runBatch(clientId, { tableName, mode: 'pending', productIds });
        setActiveRunId(runId);
        setActivityStep('Esperando primer progreso del backend…');
      } else {
        // 'pending' o 'all'
        setActivityStep(batchMode === 'all'
          ? 'Lanzando re-catalogación de TODOS los productos…'
          : 'Lanzando catalogación de pendientes…');
        const { runId } = await runBatch(clientId, { tableName, mode: batchMode });
        setActiveRunId(runId);
        setActivityStep('Esperando primer progreso del backend…');
      }
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
      setActivityStep(null);
    } finally {
      setLoading(false);
    }
  };

  // Limpia activityStep cuando llega el primer dato del run.
  useEffect(() => {
    if (run && activityStep) setActivityStep(null);
    // eslint-disable-next-line
  }, [run]);


  const handleCancel = async () => {
    if (!activeRunId) return;
    try { await cancelRun(clientId, activeRunId); } catch {}
  };

  const progress = run && run.productsTotal > 0
    ? (run.productsProcessed + run.productsFailed) / run.productsTotal
    : 0;

  // Cálculos derivados para métricas de tiempo/velocidad/ETA.
  // Todo se calcula en frontend sobre datos que ya devuelve `getRun`.
  const isProcessing = run?.status === 'processing';
  const isFinished = run && (run.status === 'completed' || run.status === 'cancelled' || run.status === 'error');

  const startedAtMs = startedAtMsRef.current ?? (run?.triggeredAt ? Date.parse(run.triggeredAt) : null);
  const completedAtMs = run?.completedAt ? Date.parse(run.completedAt) : null;

  const liveProcessed = run ? run.productsProcessed + run.productsFailed : 0;
  const elapsedMs = isProcessing && startedAtMs ? nowTick - startedAtMs : 0;

  // Velocidad rolling: usar último checkpoint anterior al actual (penúltimo) como base.
  // Si no hay 2 checkpoints aún, usar startedAt como base (acumulado total desde el inicio).
  let recentSpeed = 0; // prod/s
  if (isProcessing && startedAtMs) {
    const cps = checkpointsRef.current;
    const baseCheckpoint = cps.length >= 2
      ? cps[cps.length - 2]            // último cerrado, ventana = ~últimos 1000
      : { count: 0, ts: startedAtMs };  // fallback: desde el inicio
    const dCount = liveProcessed - baseCheckpoint.count;
    const dMs = nowTick - baseCheckpoint.ts;
    if (dCount > 0 && dMs > 0) {
      recentSpeed = dCount / (dMs / 1000);
    }
  }

  const remaining = run ? Math.max(0, run.productsTotal - liveProcessed) : 0;
  const etaMs = isProcessing && recentSpeed > 0 ? (remaining / recentSpeed) * 1000 : null;

  // Métricas finales (al terminar el run).
  const totalElapsedMs = (startedAtMs && completedAtMs) ? completedAtMs - startedAtMs : 0;
  const finalProcessed = run ? run.productsProcessed + run.productsFailed : 0;
  const msPerProduct = finalProcessed > 0 ? totalElapsedMs / finalProcessed : 0;
  const msPer1000 = msPerProduct * 1000;

  // -------------------------------------------------------------------------
  // Derived: 4 stat cards + 1 extra (manually edited).
  // -------------------------------------------------------------------------
  const total = stats?.total ?? 0;
  const cataloged = stats?.cataloged ?? 0;
  const pendingUpload = stats?.iaSplit?.withIaPendingUpload ?? 0;
  // "Pendientes catalogado" = sin LLM. Si tenemos iaSplit, lo tomamos exacto.
  // Si no, fallback a (total - cataloged).
  const pendingCatalog = stats?.iaSplit?.pendingNoIa ?? (total - cataloged);
  const manuallyEdited = tomaMeta?.manuallyEditedCount ?? 0;
  const pct = (n: number) => (total > 0 ? ((n / total) * 100).toFixed(1) + '%' : '—');

  // Costo estimado (avg ≈ $0.00012/prod con prompt+inputs medianos; fallback genérico)
  // Si tenemos último run con datos, lo usamos. Sino, default conservador.
  const avgCostPerProduct = 0.00012;
  const itemsToProcess =
    batchMode === 'sample' ? sampleSize :
    batchMode === 'pending' ? pendingCatalog :
    total;
  const estimatedCost = itemsToProcess * avgCostPerProduct;

  // Reactividad del default cantidad: opción (a) — auto-refresh si user no tocó input.
  // Para modo "sample" no aplica (el preset es manual). Aplica al display readonly de
  // pending/all, que ya derive directamente de stats — sin estado local que persista.

  // Wrapper para setSampleSize que marca touched (cuando viene de input directo).
  const setSampleSizeManual = (n: number) => {
    userTouchedSampleRef.current = true;
    setSampleSize(n);
  };

  const formatDate = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('es-AR');
  };
  const formatDateTime = (iso: string | null): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('es-AR', { hour12: false });
  };

  return (
    <div className="space-y-3">
      {/* Top section: 2-col layout 60/40 — Toma (izq) + Config en uso (der) */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {/* Card Toma — 60% (col-span-3 de 5) */}
        <div className="md:col-span-3 border border-slate-200 rounded-lg p-4 bg-gradient-to-br from-slate-50 to-white flex flex-col">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Toma</div>

          {/* Dropdown TOMA + botón refresh con mismo estilo del primary "Iniciar Batch" */}
          <div className="flex items-stretch gap-2">
            <select
              value={tableName}
              onChange={e => setTableName(e.target.value)}
              className="text-sm border border-gray-300 rounded px-2 py-1.5 flex-1 bg-white"
            >
              {tables.map(t => <option key={t} value={t}>{tomaLabel(t)}</option>)}
            </select>
            <button
              onClick={refreshStats}
              disabled={statsLoading || !tableName}
              title="Refrescar totales, pendientes y metadata"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded disabled:bg-gray-300 transition-colors"
            >
              {statsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refrescar
            </button>
          </div>

          {/* 4 stat cards: TOTAL / CATALOGADOS / PEND SUBIDA / PEND CATALOGADO.
              Placeholder soft (·) en text-slate-300 cuando aún no llegó stats. */}
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="border border-slate-200 rounded px-2 py-2 bg-slate-50">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total</div>
              <div className="text-xl font-bold tabular-nums text-slate-900 leading-tight">
                {stats ? total.toLocaleString('es-AR') : <span className="text-slate-300 font-light">·</span>}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">items en la toma</div>
            </div>
            <div className="border border-emerald-200 rounded px-2 py-2 bg-emerald-50">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Catalogados</div>
              <div className="text-xl font-bold tabular-nums text-emerald-800 leading-tight">
                {stats ? cataloged.toLocaleString('es-AR') : <span className="text-emerald-300 font-light">·</span>}
              </div>
              <div className="text-[10px] text-emerald-600 mt-0.5 tabular-nums">
                {stats ? `${pct(cataloged)} del total` : <span className="text-emerald-300">·</span>}
              </div>
            </div>
            <div className="border border-amber-200 rounded px-2 py-2 bg-amber-50" title="LLM ejecutado pero sin INSERT a catalog en MariaDB">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700">Pend. subida</div>
              <div className="text-xl font-bold tabular-nums text-amber-800 leading-tight">
                {stats?.iaSplit ? pendingUpload.toLocaleString('es-AR') : <span className="text-amber-300 font-light">·</span>}
              </div>
              <div className="text-[10px] text-amber-600 mt-0.5 tabular-nums">
                {stats?.iaSplit ? `${pct(pendingUpload)} del total` : <span className="text-amber-300">·</span>}
              </div>
            </div>
            <div className="border border-red-200 rounded px-2 py-2 bg-red-50" title="Sin LLM ejecutado — nunca pasaron por IA">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-red-700">Pend. catalogado</div>
              <div className="text-xl font-bold tabular-nums text-red-800 leading-tight">
                {stats ? pendingCatalog.toLocaleString('es-AR') : <span className="text-red-300 font-light">·</span>}
              </div>
              <div className="text-[10px] text-red-600 mt-0.5 tabular-nums">
                {stats ? `${pct(pendingCatalog)} del total` : <span className="text-red-300">·</span>}
              </div>
            </div>
          </div>

          {/* Caja extra: manually_edited_fields (intervención humana) */}
          {manuallyEdited > 0 && (
            <div className="mt-2 inline-flex items-center gap-1.5 self-start px-2 py-1 text-[11px] bg-violet-50 border border-violet-200 rounded">
              <Pencil className="w-3 h-3 text-violet-700" />
              <span className="text-violet-800">
                <span className="font-semibold tabular-nums">{manuallyEdited.toLocaleString('es-AR')}</span>
                <span className="text-violet-600"> con edición manual</span>
              </span>
            </div>
          )}

          {/* Metadata de la toma: fecha + sitios + última update */}
          <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1 text-[11px] text-slate-600">
            <div>
              <span className="text-slate-500">Fecha toma:</span>{' '}
              <span className="font-medium text-slate-800 tabular-nums">{formatDate(tomaMeta?.tomaDate ?? null)}</span>
            </div>
            <div title={tomaMeta?.sites?.join(', ') || ''}>
              <span className="text-slate-500">Sitios:</span>{' '}
              <span className="font-medium text-slate-800 tabular-nums">{tomaMeta?.sitesCount ?? <span className="text-slate-300 font-light">·</span>}</span>
            </div>
            <div>
              <span className="text-slate-500">Última cataloga.:</span>{' '}
              <span className="font-medium text-slate-800 tabular-nums">{formatDateTime(tomaMeta?.lastCatalogUpdate ?? null)}</span>
            </div>
          </div>
        </div>

        {/* Card Config en uso — 40% (col-span-2 de 5) */}
        <div className="md:col-span-2 border border-slate-200 rounded-lg p-4 bg-gradient-to-br from-slate-50 to-white flex flex-col">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Config en uso</div>
          {activePrompt ? (
            <>
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="text-sm font-bold text-slate-900">v{activePrompt.version}</span>
                {activePrompt.name && (
                  <span className="text-xs text-slate-500 truncate">· {activePrompt.name}</span>
                )}
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">provider</span>
                  <span className="font-medium text-slate-800 truncate">{activePrompt.provider}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">modelo</span>
                  <span className="font-medium text-slate-800 truncate">{activePrompt.model}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">concurrencia</span>
                  <span className="font-medium tabular-nums text-slate-800">{activePrompt.concurrency ?? 5}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">temp / max</span>
                  <span className="font-medium tabular-nums text-slate-800">
                    {(activePrompt.temperature ?? 0.3).toFixed(2)} / {activePrompt.maxTokens ?? 2000}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-500">conf threshold</span>
                  <span className="font-medium tabular-nums text-slate-800">{(activePrompt.confidenceThreshold ?? 0.7).toFixed(2)}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-xs text-slate-400 italic">Sin prompt activo</div>
          )}
        </div>
      </div>

      {/* Sección CATALOGACION rediseñada:
          - Ancho ajustado al box Toma de arriba (col-span-3 de 5)
          - Espacio derecho 40% queda libre para futura info
          - Mode pills + Cantidad apilados ocupando todo el ancho del box
          - Botón "Iniciar Batch" anclado abajo a la derecha */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-3 border border-gray-200 rounded-lg bg-gradient-to-br from-blue-50/60 to-white flex flex-col">
          {/* min-height fija para evitar salto de layout al cambiar de modo.
              Calculada para acomodar el caso más alto (sample con presets + estratificado).
              Body p-3 (24px) + Modo (~56px) + space-y-3 (12px) + label Cantidad (~18px) +
              input row (~34px) + checkbox row (~20px + mt-2 8px) ≈ 172px → redondeo 180px */}
          <div className="p-3 space-y-3 flex-1" style={{ minHeight: 180 }}>
            {/* Mode selector (pills grandes) */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Modo</div>
              <div className="grid grid-cols-3 gap-1.5">
                <button
                  onClick={() => setBatchMode('sample')}
                  disabled={loading || !!activeRunId}
                  title="Random N pendientes para QA del prompt. Persisten en catalog, no se re-procesan."
                  className={`px-2 py-2 text-xs rounded border transition-colors ${
                    batchMode === 'sample'
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold">Muestra</div>
                  <div className={`text-[10px] mt-0.5 ${batchMode === 'sample' ? 'text-blue-100' : 'text-gray-500'}`}>QA del prompt</div>
                </button>
                <button
                  onClick={() => setBatchMode('pending')}
                  disabled={loading || !!activeRunId}
                  title="Todos los productos sin entrada en catalog. Idempotente y reanudable."
                  className={`px-2 py-2 text-xs rounded border transition-colors ${
                    batchMode === 'pending'
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold">Pendientes</div>
                  <div className={`text-[10px] mt-0.5 ${batchMode === 'pending' ? 'text-emerald-100' : 'text-gray-500'}`}>Solo no catalogados</div>
                </button>
                <button
                  onClick={() => setBatchMode('all')}
                  disabled={loading || !!activeRunId}
                  title="Re-cataloga TODO sobreescribiendo existentes, excepto campos editados manualmente."
                  className={`px-2 py-2 text-xs rounded border transition-colors ${
                    batchMode === 'all'
                      ? 'bg-red-600 text-white border-red-600 shadow-sm'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold">Todo</div>
                  <div className={`text-[10px] mt-0.5 ${batchMode === 'all' ? 'text-red-100' : 'text-gray-500'}`}>Re-cataloga</div>
                </button>
              </div>
            </div>

            {/* Cantidad — debajo de Modo */}
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
                {batchMode === 'sample' ? 'Cantidad a procesar' : 'A procesar'}
              </div>
              {batchMode === 'sample' ? (
                <>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <input
                      type="number"
                      min={1}
                      step={50}
                      value={sampleSize}
                      onChange={e => setSampleSizeManual(Math.max(1, parseInt(e.target.value) || 0))}
                      disabled={loading || !!activeRunId}
                      className="text-sm border border-gray-300 rounded px-2 py-1.5 w-24 tabular-nums"
                    />
                    <span className="text-[11px] text-gray-500">ítems</span>
                    <div className="flex flex-wrap gap-1 ml-2">
                      {[50, 100, 500, 1000, 5000].map(n => (
                        <button
                          key={n}
                          onClick={() => setSampleSizeManual(n)}
                          disabled={loading || !!activeRunId}
                          className={`text-[11px] px-2 py-0.5 rounded border ${sampleSize === n ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50 bg-white'}`}
                        >
                          {n.toLocaleString('es-AR')}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer mt-2">
                    <input
                      type="checkbox"
                      checked={useStratified}
                      onChange={e => setUseStratified(e.target.checked)}
                      disabled={loading || !!activeRunId}
                    />
                    Estratificado por Cat (≥15% c/u)
                  </label>
                </>
              ) : (
                <div className="text-sm">
                  <div className="font-bold tabular-nums text-gray-900">
                    {itemsToProcess.toLocaleString('es-AR')}
                    <span className="text-xs text-gray-500 font-normal"> ítems</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer del box: costo estimado a la izq + acción abajo-derecha */}
          <div className="px-3 pb-3 pt-1 flex items-end justify-between gap-2">
            <div className="text-[11px] text-gray-500 flex-1">
              Costo estimado:{' '}
              <span className="font-semibold text-gray-800 tabular-nums">
                ${estimatedCost.toFixed(estimatedCost < 1 ? 4 : 2)}
              </span>
              <span className="text-gray-400"> · {itemsToProcess.toLocaleString('es-AR')} ítems</span>
            </div>
            <div className="flex flex-col gap-1.5 items-end shrink-0">
              <button
                onClick={startUnified}
                disabled={loading || !!activeRunId || !tableName || (batchMode === 'sample' && sampleSize <= 0)}
                className={`flex items-center justify-center gap-1.5 px-4 py-2 text-sm text-white font-semibold rounded disabled:bg-gray-300 shadow-sm ${
                  batchMode === 'all' ? 'bg-red-600 hover:bg-red-700'
                  : batchMode === 'pending' ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (batchMode === 'all' ? <AlertTriangle className="w-4 h-4" /> : <Play className="w-4 h-4" />)}
                Iniciar Batch
              </button>
              {activeRunId && (
                <button onClick={handleCancel}
                        className="flex items-center justify-center gap-1.5 px-3 py-1 text-xs border border-gray-300 hover:bg-gray-50 rounded">
                  <X className="w-3 h-3" /> Cancelar
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Columna derecha 40% — vacía por ahora (reservada para info futura) */}
        <div className="md:col-span-2"></div>
      </div>

      {/* Activity step: feedback inmediato mientras arranca el run, antes del primer poll */}
      {activityStep && !run && (
        <div className="border border-blue-200 rounded-lg p-2.5 bg-blue-50 flex items-center gap-2 text-xs text-blue-900">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>{activityStep}</span>
        </div>
      )}

      {errMsg && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{errMsg}</div>}

      {run && (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="font-semibold">{run.status}</span>
            <div className="flex items-center gap-3">
              <span className="tabular-nums text-gray-600">
                {liveProcessed} de {run.productsTotal}
                {run.productsTotal > 0 && ` (${Math.round(progress * 100)}%)`}
              </span>
              <button
                onClick={() => setInspectOpen(true)}
                className="flex items-center gap-1 px-2 py-0.5 text-[11px] border border-gray-300 hover:bg-white rounded"
                title="Inspeccionar ítems del run"
              >
                <Search className="w-3 h-3" /> Inspeccionar ítems
              </button>
            </div>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress * 100}%` }} />
          </div>

          {/* Métricas en vivo: elapsed / velocidad / ETA. Solo durante processing. */}
          {isProcessing && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-700 tabular-nums">
              <div>
                <span className="text-gray-500">Elapsed:</span>{' '}
                <span className="font-semibold text-gray-900">{formatDuration(elapsedMs)}</span>
              </div>
              <div className="text-gray-300">·</div>
              <div>
                <span className="text-gray-500">Velocidad:</span>{' '}
                <span className="font-semibold text-gray-900">{formatSpeed(recentSpeed)}</span>
              </div>
              <div className="text-gray-300">·</div>
              <div>
                <span className="text-gray-500">ETA:</span>{' '}
                <span className="font-semibold text-gray-900">
                  {etaMs != null ? `${formatDuration(etaMs)} restantes` : '—'}
                </span>
              </div>
            </div>
          )}

          <div className="mt-2 grid grid-cols-4 gap-2 text-[11px] text-gray-600 tabular-nums">
            <div>OK: <span className="font-semibold text-gray-900">{run.productsProcessed}</span></div>
            <div>Errores: <span className="font-semibold text-red-700">{run.productsFailed}</span></div>
            <div>Tokens: <span className="font-semibold text-gray-900">{run.tokensIn}/{run.tokensOut}</span></div>
            <div>Costo: <span className="font-semibold text-gray-900">${(run.costUsd || 0).toFixed(4)}</span></div>
          </div>
          <div className="mt-1 grid grid-cols-3 gap-2 text-[11px] text-gray-500">
            <div>Hallucinations: {run.validation?.hallucinationsDetected || 0}</div>
            <div>Below threshold: {run.validation?.belowThreshold || 0}</div>
            <div>Cat no-leaf: {run.validation?.categoryNotLeaf || 0}</div>
          </div>

          {/* Bloque resumen al finalizar. */}
          {isFinished && totalElapsedMs > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-[11px] font-semibold text-gray-700 uppercase mb-1.5">Resumen</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-[11px] tabular-nums">
                <div>
                  <span className="text-gray-500">Tiempo total:</span>{' '}
                  <span className="font-semibold text-gray-900">{formatDuration(totalElapsedMs)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Productos:</span>{' '}
                  <span className="font-semibold text-gray-900">{finalProcessed.toLocaleString('es-AR')}</span>
                </div>
                <div>
                  <span className="text-gray-500">Por producto:</span>{' '}
                  <span className="font-semibold text-gray-900">
                    {msPerProduct >= 1000
                      ? `${(msPerProduct / 1000).toFixed(2)} s/prod`
                      : `${Math.round(msPerProduct)} ms/prod`}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Por cada 1000:</span>{' '}
                  <span className="font-semibold text-gray-900">{formatDuration(msPer1000)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Tokens IN:</span>{' '}
                  <span className="font-semibold text-gray-900">
                    {run.tokensIn.toLocaleString('es-AR')}
                    {finalProcessed > 0 && (
                      <span className="text-gray-500 font-normal"> (avg {Math.round(run.tokensIn / finalProcessed).toLocaleString('es-AR')}/prod)</span>
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Tokens OUT:</span>{' '}
                  <span className="font-semibold text-gray-900">
                    {run.tokensOut.toLocaleString('es-AR')}
                    {finalProcessed > 0 && (
                      <span className="text-gray-500 font-normal"> (avg {Math.round(run.tokensOut / finalProcessed).toLocaleString('es-AR')}/prod)</span>
                    )}
                  </span>
                </div>
                <div className="col-span-2 md:col-span-3">
                  <span className="text-gray-500">Costo total:</span>{' '}
                  <span className="font-semibold text-gray-900">${(run.costUsd || 0).toFixed(4)}</span>
                  {finalProcessed > 0 && (
                    <span className="text-gray-500 font-normal"> (~${(run.costUsd / finalProcessed).toFixed(6)}/prod)</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {inspectOpen && run && (
        <InspectItemsModal
          clientId={clientId}
          run={run}
          onClose={() => setInspectOpen(false)}
        />
      )}
    </div>
  );
};

// =============================================================================
// CAMBIO #8: REPORTS SUBTAB — Brand Curation Reports
// =============================================================================

const ReportsSubtab: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [reports, setReports] = useState<CurationReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setErrMsg(null);
    try {
      const { reports } = await listCurationReports(clientId, { limit: 100 });
      setReports(reports);
    } catch (e: any) {
      setErrMsg(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [clientId]);

  const handleStatusChange = async (runId: string, status: 'pending_review' | 'sent' | 'archived') => {
    try {
      await patchCurationReport(clientId, runId, { status });
      await reload();
    } catch (e: any) {
      alert('Error: ' + (e?.response?.data?.error || e.message));
    }
  };

  const handleExportCSV = (r: CurationReport) => {
    // Construir CSV con secciones
    const lines: string[] = [];
    lines.push('Section,Brand/Subcat,LLM Brand,Count,Examples');

    for (const e of r.brandsFromLlmOnly?.notWhitelisted || []) {
      lines.push(`marcas_nuevas_no_whitelist,"${e.brand}",,${e.count},"${(e.examples || []).join(' | ')}"`);
    }
    for (const e of r.brandsFromLlmOnly?.whitelisted || []) {
      lines.push(`marcas_nuevas_whitelist,"${e.brand}",,${e.count},"${(e.examples || []).join(' | ')}"`);
    }
    for (const e of r.brandsDisagreed || []) {
      lines.push(`discrepancias,"${e.tomaBrand}","${e.llmBrand}",${e.count},"${(e.examples || []).join(' | ')}"`);
    }
    for (const e of r.subcategorySuggestions || []) {
      lines.push(`subcats_sugeridas,"${e.suggested}",,${e.count},"${(e.examples || []).join(' | ')}"`);
    }
    for (const e of r.brandsTomaNotWhitelisted || []) {
      lines.push(`toma_no_whitelist,"${e.brand}",,${e.count},"${(e.examples || []).join(' | ')}"`);
    }

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `curation_report_${r.runId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-sm text-gray-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" />Cargando...</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">Reportes de curación generados automáticamente al finalizar cada run.</div>
        <button onClick={reload} className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">
          <RefreshCw className="w-3 h-3" /> Refrescar
        </button>
      </div>

      {errMsg && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{errMsg}</div>}

      {reports.length === 0 && (
        <div className="text-sm text-gray-500 italic p-4 border border-dashed border-gray-300 rounded">
          Sin reportes aún. Generá uno corriendo un batch o test desde el banco.
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-2 py-1.5 text-left">Fecha</th>
              <th className="px-2 py-1.5 text-left">Tipo</th>
              <th className="px-2 py-1.5 text-left">Versión</th>
              <th className="px-2 py-1.5 text-right">Total</th>
              <th className="px-2 py-1.5 text-right">Nuevas</th>
              <th className="px-2 py-1.5 text-right">Discrep.</th>
              <th className="px-2 py-1.5 text-right">Subcats</th>
              <th className="px-2 py-1.5 text-left">Status</th>
              <th className="px-2 py-1.5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {reports.map(r => {
              const isTest = r.runType === 'test' || r.runType === 'manual_test';
              const isExp = expanded === r.runId;
              const newCount = (r.brandsFromLlmOnly?.notWhitelisted?.length || 0) + (r.brandsFromLlmOnly?.whitelisted?.length || 0);
              const disagrCount = (r.brandsDisagreed || []).length;
              const subcatCount = (r.subcategorySuggestions || []).length;
              return (
                <React.Fragment key={r.runId}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-2 py-1.5 tabular-nums">{r.runDate ? new Date(r.runDate).toLocaleString('es-AR', { hour12: false }) : '—'}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isTest ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {r.runType}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">v{formatSemverVersion(r.promptVersion)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{r.totalProducts}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{newCount}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{disagrCount}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{subcatCount}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        r.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                        r.status === 'archived' ? 'bg-gray-200 text-gray-600' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => setExpanded(isExp ? null : r.runId)}
                              className="text-[10px] px-1.5 py-0.5 border border-gray-300 hover:bg-gray-100 rounded">
                        {isExp ? 'Cerrar' : 'Ver detalle'}
                      </button>
                      <button onClick={() => handleExportCSV(r)}
                              className="ml-1 text-[10px] px-1.5 py-0.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded inline-flex items-center gap-1">
                        <Download className="w-3 h-3" /> CSV
                      </button>
                      {r.status !== 'sent' && (
                        <button onClick={() => handleStatusChange(r.runId, 'sent')}
                                className="ml-1 text-[10px] px-1.5 py-0.5 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded inline-flex items-center gap-1">
                          <Send className="w-3 h-3" /> Enviado
                        </button>
                      )}
                      {r.status !== 'archived' && (
                        <button onClick={() => handleStatusChange(r.runId, 'archived')}
                                className="ml-1 text-[10px] px-1.5 py-0.5 border border-gray-300 hover:bg-gray-100 rounded inline-flex items-center gap-1">
                          <Archive className="w-3 h-3" /> Archivar
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExp && (
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <td colSpan={9} className="p-3">
                        <ReportDetail report={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReportDetail: React.FC<{ report: CurationReport }> = ({ report }) => {
  return (
    <div className="space-y-3 text-xs">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] uppercase text-gray-500">Consistentes</div>
          <div className="text-lg font-semibold text-emerald-700 tabular-nums">{report.brandsConsistent}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] uppercase text-gray-500">Toma only</div>
          <div className="text-lg font-semibold text-blue-700 tabular-nums">{report.brandsTomaOnly}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] uppercase text-gray-500">Sin marca</div>
          <div className="text-lg font-semibold text-gray-700 tabular-nums">{report.brandsAllNull}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2">
          <div className="text-[10px] uppercase text-gray-500">Total</div>
          <div className="text-lg font-semibold text-gray-900 tabular-nums">{report.totalProducts}</div>
        </div>
      </div>

      <ReportListSection title="Marcas nuevas (LLM only — fuera de whitelist)"
                         items={(report.brandsFromLlmOnly?.notWhitelisted || []).map(e => ({ key: e.brand, count: e.count, examples: e.examples }))} />
      <ReportListSection title="Marcas nuevas (LLM only — whitelisted)"
                         items={(report.brandsFromLlmOnly?.whitelisted || []).map(e => ({ key: e.brand, count: e.count, examples: e.examples }))} />
      <ReportListSection title="Discrepancias toma vs LLM"
                         items={(report.brandsDisagreed || []).map(e => ({ key: `${e.tomaBrand} → ${e.llmBrand}`, count: e.count, examples: e.examples }))} />
      <ReportListSection title="Subcategorías sugeridas (no en matriz)"
                         items={(report.subcategorySuggestions || []).map(e => ({ key: e.suggested, count: e.count, examples: e.examples }))} />
      <ReportListSection title="Marcas de toma fuera de whitelist"
                         items={(report.brandsTomaNotWhitelisted || []).map(e => ({ key: e.brand, count: e.count, examples: e.examples }))} />
    </div>
  );
};

const ReportListSection: React.FC<{ title: string; items: { key: string; count: number; examples?: string[] }[] }> = ({ title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded p-2">
      <div className="text-[11px] font-semibold text-gray-700 uppercase mb-1">{title} ({items.length})</div>
      <div className="space-y-1 max-h-[160px] overflow-y-auto">
        {items.map((it, i) => (
          <div key={i} className="flex items-start justify-between gap-2 border-b border-gray-50 last:border-b-0 pb-1">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-800">{it.key}</div>
              {it.examples && it.examples.length > 0 && (
                <div className="text-[10px] text-gray-500">
                  {it.examples.slice(0, 2).map((e, ei) => <div key={ei} className="truncate" title={e}>{e}</div>)}
                </div>
              )}
            </div>
            <span className="shrink-0 text-[11px] tabular-nums bg-gray-100 px-1.5 py-0.5 rounded">×{it.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
